import { Button, Text, View } from "@voicerun/design-system";
import { useLocation, useNavigate } from "react-router-dom";
import * as Diff from "diff";
import * as monaco from "monaco-editor";
import clsx from "clsx";
import Editor from "@monaco-editor/react";
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import ReactMarkdown from "react-markdown";

import { agentEnvironmentActions } from "../../redux/reducers/agentEnvironmentSlice";
import { agentFunctionActions } from "../../redux/reducers/agentFunctionSlice";
import { ConfirmDialog } from "../ConfirmDialog";
import { Dialog, FileField, FolderUploadField, InputField } from "../base";
import { featureActions } from "../../redux/reducers/featureSlice";
import { Icon } from "../base/Icon";
import { makeRequest, makeStreamingRequest } from "../../utils/request";
import { python } from "../../utils/editor";
import { PYTHON_FUNCTION_TEMPLATE, JAVASCRIPT_FUNCTION_TEMPLATE } from "../../pages/agent/AgentFunctionPage";
import { useAppDispatch, useAppSelector } from "../../redux";
import { useTheme } from "../../contexts/ThemeContext";
import { zipFiles, unzipCode } from "../../utils/files";
import { useAuth } from "../../hooks/useAuth";

const shouldIgnoreFile = (filePath: string): boolean => {
  const ignorePatterns = [
    // Virtual environments
    ".venv",
    "venv",
    "env",
    "ENV",
    "env.bak",
    "venv.bak",

    // Python cache and compiled files
    "__pycache__",
    ".pyc",
    ".pyo",
    ".pyd",
    ".pyo",
    ".pyd",

    // System files
    ".DS_Store",
    "Thumbs.db",
    "desktop.ini",
    ".Spotlight-V100",
    ".Trashes",
    "ehthumbs.db",

    // Git
    ".git",
    ".gitignore",
    ".gitattributes",
    ".gitmodules",

    // Package managers
    "uv.lock",
    "package-lock.json",
    "yarn.lock",
    "pnpm-lock.yaml",
    "poetry.lock",
    "Pipfile.lock",

    // Archives
    "*.zip",
    "*.tar.gz",
    "*.rar",
    "*.7z",

    // IDE and editor files
    ".vscode",
    ".idea",
    "*.swp",
    "*.swo",
    "*~",
    ".sublime-project",
    ".sublime-workspace",

    // Logs and temporary files
    "*.log",
    "*.tmp",
    "*.temp",
    ".tmp",
    ".temp",

    // Node.js
    "node_modules",
    "npm-debug.log*",
    "yarn-debug.log*",
    "yarn-error.log*",

    // Build and distribution
    "dist",
    "build",
    "*.egg-info",
    "__pycache__",
    ".pytest_cache",
    ".coverage",
    "htmlcov",

    // Environment files
    ".env",
    ".env.local",
    ".env.development",
    ".env.test",
    ".env.production",
  ];

  const pathParts = filePath.toLowerCase().split("/");
  for (const part of pathParts) {
    for (const pattern of ignorePatterns) {
      // Handle wildcard patterns
      if (pattern.includes("*")) {
        const regexPattern = pattern.replace(/\*/g, ".*");
        if (new RegExp(regexPattern).test(part)) {
          return true;
        }
      } else if (part === pattern.toLowerCase()) {
        return true;
      }
    }
  }
  return false;
};

export interface FilesState {
  [filename: string]: {
    content: string;
    language: string;
  };
}

type AgentFunctionEditorAdvancedProps = {
  code?: string;
  isMultifile?: boolean;
  files?: FilesState;
  activeFile?: string;
  onChange: (files: FilesState) => void;
  onActiveFileChange?: (filename: string) => void;
  readOnly?: boolean;
  language?: string;
  functionId?: string;
  latestNonDeployedVersionName?: string;
};

export const AgentFunctionEditorAdvanced = ({
  code: propCode,
  isMultifile: propIsZip,
  files: propFiles,
  activeFile: propActiveFile,
  onChange,
  onActiveFileChange,
  readOnly = false,
  language = "python",
  functionId,
  latestNonDeployedVersionName,
}: AgentFunctionEditorAdvancedProps) => {
  const { theme } = useTheme();
  const { isAdmin } = useAuth();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const agentFunctionFormData = useAppSelector((state) => state.feature.agentFunctionFormData);
  const pendingAssistantAction = useAppSelector((state) => (state.feature as any).agentFunctionPendingAssistantAction);
  const agentEnvironments = useAppSelector((state) => Object.values((state as any).agentEnvironment.entities || {}));
  const [autoCreatedFromReadOnly, setAutoCreatedFromReadOnly] = useState<null | { previousEnvironmentId?: string }>(null);
  const [showAutoDeployDialog, setShowAutoDeployDialog] = useState(false);

  // Add CSS for diff highlighting
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .diff-removed-line {
        background-color: #ffebee !important;
        text-decoration: line-through;
        opacity: 0.7;
      }
      .diff-added-line {
        background-color: #e8f5e8 !important;
        font-weight: 500;
      }
      .diff-removed-marker::before {
        content: "-";
        color: #d32f2f;
        font-weight: bold;
        margin-right: 8px;
      }
      .diff-added-marker::before {
        content: "+";
        color: #388e3c;
        font-weight: bold;
        margin-right: 8px;
      }
      [data-theme="dark"] .diff-removed-line {
        background-color: #3d1f1f !important;
      }
      [data-theme="dark"] .diff-added-line {
        background-color: #1f3d1f !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const isInitializedRef = useRef(false);
  const lastProcessedCodeRef = useRef<string | undefined>();
  const prevFunctionIdRef = useRef<string | undefined>();

  // State for UI
  const [showEditor, setShowEditor] = useState(true);
  const [showFileExplorer, setShowFileExplorer] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMode, setSearchMode] = useState<"files" | "content">("content");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(192); // 192px = w-48 default
  const [isResizing, setIsResizing] = useState(false);
  const [expandedSearchFiles, setExpandedSearchFiles] = useState<Set<string>>(new Set());
  const [fontSize, setFontSize] = useState(14);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [selectedNode, setSelectedNode] = useState<string>("");
  const [focusedNode, setFocusedNode] = useState<string>("");
  const [activeFolder, setActiveFolder] = useState<string | null>(null); // Track which folder is active for new files
  const [openTabs, setOpenTabs] = useState<string[]>([]); // Track which files are open in tabs
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    targetFile: string;
  }>({
    visible: false,
    x: 0,
    y: 0,
    targetFile: "",
  });
  const [renamingFile, setRenamingFile] = useState<string>("");
  const [renameValue, setRenameValue] = useState<string>("");
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [tabDropTarget, setTabDropTarget] = useState<{ filename: string; position: "before" | "after" } | null>(null);
  const [showAddFileModal, setShowAddFileModal] = useState(false);
  const [modalFileName, setModalFileName] = useState("");
  const [modalTab, setModalTab] = useState<"create" | "upload">("create");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  // Add state for project upload modal
  const [showProjectUploadModal, setShowProjectUploadModal] = useState(false);
  const [uploadedProject, setUploadedProject] = useState<FileList | null>(null);
  const [addFileError, setAddFileError] = useState<string>("");
  const [renameError, setRenameError] = useState<string>("");
  const [showDeleteFolderDialog, setShowDeleteFolderDialog] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<string | null>(null);

  const [assistantGenerationResult, setAssistantGenerationResult] = useState<{
    visible: boolean;
    cost: number;
    duration: number;
    changedFiles: string[];
    originalFiles: { [filename: string]: string };
    newFiles: { [filename: string]: string };
    zipData?: string;
    labeledChangedFiles?: string[];
  } | null>(null);
  const [decoratorIds, setDecoratorIds] = useState<string[]>([]);
  const [showGenerateFunctionModal, setShowGenerateFunctionModal] = useState(false);
  const [generateFunctionPrompt, setGenerateFunctionPrompt] = useState("");
  const [showAssistantSidebar, setShowAssistantSidebar] = useState(false);
  const [assistantMessages, setAssistantMessages] = useState<Array<{ type: "user" | "assistant" | "system"; content: string }>>([]);
  const [assistantPrompt, setAssistantPrompt] = useState("");
  const [targetOutcome, setTargetOutcome] = useState("");
  const [improveTestName, setImproveTestName] = useState("");
  const [pendingImproveFromTest, setPendingImproveFromTest] = useState<string | null>(null);
  const assistantMessagesRef = useRef<HTMLDivElement>(null);
  const [isSuggestingTests, setIsSuggestingTests] = useState(false);
  const [isAddingOutcomes, setIsAddingOutcomes] = useState(false);
  const [isEditingFunction, setIsEditingFunction] = useState(false);
  const [isGeneratingFunction, setIsGeneratingFunction] = useState(false);
  const [isImprovingFunction, setIsImprovingFunction] = useState(false);
  const [hasRejectedChanges, setHasRejectedChanges] = useState(false);
  const [divergedFiles, setDivergedFiles] = useState<Set<string>>(new Set());
  const [isDeletingFolder, setIsDeletingFolder] = useState(false);

  // Global state that is true if any Assistant process is generating
  const isAssistantGenerating = isSuggestingTests || isAddingOutcomes || isEditingFunction || isGeneratingFunction || isImprovingFunction;

  // Helper functions to reduce code duplication
  const prepareZipData = async (): Promise<string> => {
    const zipBlob = await zipFiles(files);
    const fileReader = new FileReader();
    return new Promise<string>((resolve) => {
      fileReader.onload = () => {
        const base64 = fileReader.result as string;
        const base64Data = base64.split(",")[1];
        resolve(base64Data);
      };
      fileReader.readAsDataURL(zipBlob);
    });
  };

  const getAgentIdFromPath = (): string | null => {
    try {
      const pathParts = window.location.pathname.split("/");
      return pathParts[2] || null; // /agents/{agentId}/function
    } catch {
      return null;
    }
  };

  const getImproveFromTestFromUrl = (): string | null => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get("improveFromTest");
    } catch {
      return null;
    }
  };

  // Capture improve-from-test param on mount and populate input
  useEffect(() => {
    const improveFromTest = getImproveFromTestFromUrl();
    if (improveFromTest) {
      setImproveTestName(improveFromTest);
      setPendingImproveFromTest(improveFromTest);
      setShowAssistantSidebar(true);

      // Remove the improveFromTest param from the URL to prevent re-trigger on reload
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("improveFromTest");
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      } catch (e) {
        // no-op if URL manipulation fails
      }
    }
  }, []);

  // Defer auto-trigger until editor is ready (moved below file/activeFile declarations)
  // Placeholder removed here; actual effect placed after activeFile/currentFileData are defined

  const processStreamingResponse = async (
    response: Response,
    onMessage: (data: any) => void,
    onResult: (data: any) => void,
    onError: (error: string) => void,
    onEnd: () => void,
  ) => {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const streamReader = response.body?.getReader();
    if (!streamReader) {
      throw new Error("No response body reader available");
    }

    const decoder = new TextDecoder();

    while (true) {
      const { done, value } = await streamReader.read();
      if (done) break;

      const chunk = decoder.decode(value);
      const lines = chunk.split("\n");

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === "user" && data.message && data.message.trim()) {
              onMessage(data);
            } else if (data.type === "assistant" && data.message) {
              onMessage(data);
            } else if (data.type === "system" && data.message) {
              onMessage(data);
            } else if (data.type === "result") {
              onResult(data);
            } else if (data.type === "end") {
              onEnd();
            } else if (data.type === "error") {
              onError(data.message);
            }
          } catch (parseError) {
            console.error("Error parsing streaming data:", parseError);
          }
        }
      }
    }
  };

  const handleAssistantStreamingRequest = async (
    endpoint: string,
    body: any,
    stateSetter: (value: boolean) => void,
    onSuccess?: (zipData: string, cost: number, duration: number) => void,
  ) => {
    // For generateFunction, we don't need activeFile or currentFileData
    if (endpoint !== "generate-function" && (!activeFile || !currentFileData || isAssistantGenerating)) return;
    if (endpoint === "generate-function" && isAssistantGenerating) return;

    stateSetter(true);
    // Don't clear messages - keep conversation history

    try {
      const zipData = await prepareZipData();
      const urlFunctionId = functionId;
      const agentId = getAgentIdFromPath();

      if (!urlFunctionId || !agentId) {
        // Cannot proceed without identifiers
        stateSetter(false);
        return;
      }

      const response = await makeStreamingRequest(`/v1/agents/${agentId}/functions/${urlFunctionId}/${endpoint}`, {
        method: "POST",
        body: {
          functionId: urlFunctionId,
          filePath: activeFile,
          language: currentFileData?.language || "python",
          zipData: zipData,
          ...body,
        },
      });

      let finalZipData: string | undefined;
      let finalCost: number = 0;
      let finalDuration: number = 0;

      await processStreamingResponse(
        response,
        (data) => {
          // Handle user, assistant, and system messages
          if (data.message && data.message.trim()) {
            setAssistantMessages((prev) => [
              ...prev,
              {
                type: data.type as "user" | "assistant" | "system",
                content: data.message,
              },
            ]);
          }
        },
        (data) => {
          // Handle result message
          finalCost = data.total_cost_usd || 0;
          finalDuration = data.duration_ms || 0;

          if (data.zipData) {
            finalZipData = data.zipData;
            onSuccess?.(data.zipData, finalCost, finalDuration);
          }

          setAssistantMessages((prev) => [
            ...prev,
            {
              type: "system",
              content: `Operation complete! Cost: $${finalCost.toFixed(4)}, Duration: ${finalDuration}ms`,
            },
          ]);
        },
        (errorMessage) => {
          setAssistantMessages((prev) => [
            ...prev,
            {
              type: "system",
              content: `Error: ${errorMessage}`,
            },
          ]);
          stateSetter(false);
        },
        () => {
          stateSetter(false);
        },
      );
    } catch (error) {
      console.error(`Error in ${endpoint}:`, error);
      setAssistantMessages((prev) => [
        ...prev,
        {
          type: "system",
          content: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
        },
      ]);
      stateSetter(false);
    }
  };

  const updateFilesWithDiff = async (zipData: string) => {
    const unzippedFiles = await unzipCode(zipData);
    const originalFiles: { [filename: string]: string } = {};
    const updatedFiles = { ...files };

    // Track changes
    const changedFiles: string[] = [];
    const newFilesList: string[] = [];
    const deletedFilesList: string[] = [];
    const modifiedFilesList: string[] = [];

    for (const [filename, content] of Object.entries(unzippedFiles)) {
      originalFiles[filename] = files[filename]?.content || "";

      if (!files[filename]) {
        newFilesList.push(filename);
        changedFiles.push(filename);
      } else if (files[filename].content !== content) {
        modifiedFilesList.push(filename);
        changedFiles.push(filename);
      }

      updatedFiles[filename] = {
        content: content as string,
        language: getLanguageFromExtension(filename),
      };
    }

    // Check for deleted files
    for (const existingFile of Object.keys(files)) {
      if (!unzippedFiles[existingFile] && files[existingFile]) {
        deletedFilesList.push(existingFile);
        changedFiles.push(existingFile);
        originalFiles[existingFile] = files[existingFile].content;
      }
    }

    // Create labeled changed files list
    const labeledChangedFiles = changedFiles.map((file) => {
      if (newFilesList.includes(file)) {
        return `${file} (new)`;
      } else if (modifiedFilesList.includes(file)) {
        return file;
      }
      return `${file} (deleted)`;
    });

    // Show the notification bar
    setAssistantGenerationResult({
      visible: true,
      cost: 0, // Will be updated by the calling function
      duration: 0, // Will be updated by the calling function
      changedFiles,
      originalFiles,
      newFiles: unzippedFiles,
      zipData,
      labeledChangedFiles,
    });

    // Update the files state with the new content
    setFiles(updatedFiles);
    onChange(updatedFiles);

    // Reset diverged files since frontend now matches backend
    setDivergedFiles(new Set());
  };

  const replaceAllFiles = async (newFiles: FilesState) => {
    // Ensure handler file exists
    const handlerFileName = `handler.${language === "python" ? "py" : "js"}`;
    if (!newFiles[handlerFileName]) {
      newFiles[handlerFileName] = {
        content: language === "python" ? PYTHON_FUNCTION_TEMPLATE : JAVASCRIPT_FUNCTION_TEMPLATE,
        language,
      };
    }

    // Replace all files with the new content
    setFiles(newFiles);
    onChange(newFiles);

    // Reset all UI state
    setExpandedFolders(new Set());
    setSelectedNode(handlerFileName);
    setFocusedNode(handlerFileName);
    setActiveFolder(null);

    // Only call onActiveFileChange if it's different from current active file
    if (activeFile !== handlerFileName) {
      onActiveFileChange?.(handlerFileName);
    }

    // Update Redux store with zipped code
    const zipBlob = await zipFiles(newFiles);
    const reader = new FileReader();

    reader.onload = () => {
      const base64 = reader.result as string;
      const base64Data = base64.split(",")[1];

      const updatedFormData = {
        ...agentFunctionFormData,
        code: base64Data,
        codePath: true,
        isMultifile: true,
        activeFile: handlerFileName,
      };
      dispatch(featureActions.setAgentFunctionFormData(updatedFormData));
    };

    reader.readAsDataURL(zipBlob);
  };

  // Auto-scroll Assistant messages
  useEffect(() => {
    if (assistantMessagesRef.current) {
      assistantMessagesRef.current.scrollTop = assistantMessagesRef.current.scrollHeight;
    }
  }, [assistantMessages]);

  // Auto-scroll to bottom when Assistant sidebar is opened
  useEffect(() => {
    if (showAssistantSidebar && assistantMessagesRef.current) {
      // Use a small delay to ensure the sidebar is fully rendered
      setTimeout(() => {
        if (assistantMessagesRef.current) {
          assistantMessagesRef.current.scrollTop = assistantMessagesRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [showAssistantSidebar]);

  // Use provided files or initialize from code
  const [files, setFiles] = useState<FilesState>(propFiles || {});

  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize files from propCode if provided and not already initialized
  useEffect(() => {
    if (propCode && propCode !== lastProcessedCodeRef.current && !isInitializedRef.current) {
      lastProcessedCodeRef.current = propCode;

      const doUnzipCode = async () => {
        try {
          const extracted = await unzipCode(propCode);
          if (!extracted) throw new Error("No files extracted");
          const extractedFiles: FilesState = {};
          for (const [filename, content] of Object.entries(extracted)) {
            const fileLanguage = getLanguageFromExtension(filename);
            extractedFiles[filename] = { content, language: fileLanguage };
          }
          // Ensure handler file exists
          const handlerFileName = `handler.${language === "python" ? "py" : "js"}`;
          if (!extractedFiles[handlerFileName]) {
            extractedFiles[handlerFileName] = {
              content: language === "python" ? PYTHON_FUNCTION_TEMPLATE : JAVASCRIPT_FUNCTION_TEMPLATE,
              language,
            };
          }
          setFiles(extractedFiles);
          // Always open handler file first
          if (!propActiveFile) {
            onActiveFileChange?.(handlerFileName);
          }
          onChange(extractedFiles);
          isInitializedRef.current = true;
        } catch (error) {
          console.error("Error unzipping code:", error);
          // Fallback to default structure with handler file
          const handlerFileName = `handler.${language === "python" ? "py" : "js"}`;
          const fallbackFiles = {
            [handlerFileName]: {
              content: language === "python" ? PYTHON_FUNCTION_TEMPLATE : JAVASCRIPT_FUNCTION_TEMPLATE,
              language,
            },
          };
          setFiles(fallbackFiles);
          if (!propActiveFile) {
            onActiveFileChange?.(handlerFileName);
          }
          onChange(fallbackFiles);
          isInitializedRef.current = true;
        }
      };

      if (propIsZip) {
        doUnzipCode();
      } else {
        const handlerFileName = `handler.${language === "python" ? "py" : "js"}`;
        const simpleFiles = { [handlerFileName]: { content: propCode, language } };
        setFiles(simpleFiles);
        onChange(simpleFiles);
        isInitializedRef.current = true;
      }
    }
  }, [propCode, propIsZip, propActiveFile, onActiveFileChange, onChange, language]);

  // Sync files when propFiles changes
  useEffect(() => {
    if (propFiles && Object.keys(propFiles).length > 0) {
      setFiles(propFiles);
    }
  }, [propFiles]);

  // Ensure propActiveFile is always in open tabs
  useEffect(() => {
    if (propActiveFile && files[propActiveFile] && !openTabs.includes(propActiveFile)) {
      setOpenTabs((prev) => [...prev, propActiveFile]);
    }
  }, [propActiveFile, files, openTabs]);

  // Set default focus to first node
  useEffect(() => {
    if (Object.keys(files).length > 0 && !focusedNode) {
      const tree = buildFileTree(sortedFileNames);
      const filteredTree = filterTreeBySearch(tree, searchQuery);
      const visibleNodes = getVisibleNodes(filteredTree);

      if (visibleNodes.length > 0) {
        const firstNode = visibleNodes[0];
        setFocusedNode(firstNode);
        setSelectedNode(firstNode);
      }
    }
  }, [files, focusedNode]);

  // Reset state when functionId changes (indicating function switch)
  useEffect(() => {
    if (functionId && prevFunctionIdRef.current !== functionId) {
      setFiles({});
      setOpenTabs([]);
      setExpandedFolders(new Set());
      setSelectedNode("");
      setFocusedNode("");
      setActiveFolder(null);
      isInitializedRef.current = false;
      lastProcessedCodeRef.current = undefined;
      prevFunctionIdRef.current = functionId;
    }
  }, [functionId]);

  // Initialize function directory when files are loaded
  useEffect(() => {
    const initializeFunctionDirectory = async () => {
      if (functionId && Object.keys(files).length > 0 && !isInitializedRef.current) {
        try {
          // Get the agent ID from the URL path safely
          const agentId = getAgentIdFromPath();

          if (!agentId) return;

          // Create zip from current files
          const zipBlob = await zipFiles(files);
          const reader = new FileReader();
          const zipData = await new Promise<string>((resolve) => {
            reader.onload = () => {
              const base64 = reader.result as string;
              const base64Data = base64.split(",")[1];
              resolve(base64Data);
            };
            reader.readAsDataURL(zipBlob);
          });

          // Initialize function directory
          await makeRequest<{ tempDir: string }>(`/v1/agents/${agentId}/functions/${functionId}/initialize-function`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: {
              zipData,
            },
          });
        } catch (error) {
          console.error("Error initializing function directory:", error);
        }
      }
    };

    initializeFunctionDirectory();
  }, [functionId, files, isInitializedRef.current]);

  // If navigation requested an auto-improve (e.g., after creating a new version), trigger it once ready
  useEffect(() => {
    const state: any = location.state;
    if (state?.autoImproveFromTest && state?.testName) {
      if (state.previousEnvironmentId) {
        setAutoCreatedFromReadOnly({ previousEnvironmentId: state.previousEnvironmentId });
      }
      setAssistantMessages((prev) => [
        ...prev,
        {
          type: "system",
          content: `The selected function was deployed and cannot be edited directly. A new version was created and selected automatically. Proceeding to improve from test "${state.testName}". This new function is NOT deployed.`,
        },
      ]);
      setImproveTestName(state.testName);
      setPendingImproveFromTest(state.testName);
      // Clear the state so it doesn't re-trigger
      navigate(location.pathname + location.search, { replace: true, state: undefined });
    }
  }, [location.state]);

  // Simple active file logic - use prop or fallback to first open tab
  const sortedFileNames = Object.keys(files).sort();
  const activeFile = propActiveFile || openTabs[0] || "";
  const currentFileData = files[activeFile];

  // Defer auto-trigger until editor is ready (files are loaded and at least one tab is open)
  useEffect(() => {
    if (!pendingImproveFromTest) return;
    if (isAssistantGenerating) return;
    const hasFiles = Object.keys(files).length > 0;
    const hasOpenTab = openTabs.length > 0 || !!activeFile;
    if (!hasFiles || !hasOpenTab) return;
    // Trigger once
    handleImproveFunctionFromTest();
    setPendingImproveFromTest(null);
  }, [pendingImproveFromTest, isAssistantGenerating, files, openTabs, activeFile]);

  // Generic pending auto action after navigation (tests, outcomes, edit, generate)
  const [pendingAutoAction, setPendingAutoAction] = useState<null | {
    type: "generate-tests" | "generate-outcomes" | "edit-function" | "generate-function";
    payload?: any;
  }>(null);

  // Pick up additional auto actions from navigation state
  useEffect(() => {
    const state: any = location.state;
    if (!state) return;
    if (state.autoSuggestTests) {
      if (state.previousEnvironmentId) {
        setAutoCreatedFromReadOnly({ previousEnvironmentId: state.previousEnvironmentId });
      }
      setAssistantMessages((prev) => [
        ...prev,
        {
          type: "system",
          content:
            "The selected function was deployed and cannot be edited directly. A new version was created and selected automatically. Proceeding to Suggest Tests. This new function is NOT deployed.",
        },
      ]);
      setPendingAutoAction({ type: "generate-tests", payload: { targetOutcome: state.targetOutcome } });
      navigate(location.pathname + location.search, { replace: true, state: undefined });
    } else if (state.autoAddOutcomes) {
      if (state.previousEnvironmentId) {
        setAutoCreatedFromReadOnly({ previousEnvironmentId: state.previousEnvironmentId });
      }
      setAssistantMessages((prev) => [
        ...prev,
        {
          type: "system",
          content:
            "The selected function was deployed and cannot be edited directly. A new version was created and selected automatically. Proceeding to Add Outcomes. This new function is NOT deployed.",
        },
      ]);
      setPendingAutoAction({ type: "generate-outcomes" });
      navigate(location.pathname + location.search, { replace: true, state: undefined });
    } else if (state.autoEditFunction && state.userPrompt) {
      if (state.previousEnvironmentId) {
        setAutoCreatedFromReadOnly({ previousEnvironmentId: state.previousEnvironmentId });
      }
      setAssistantMessages((prev) => [
        ...prev,
        {
          type: "system",
          content:
            "The selected function was deployed and cannot be edited directly. A new version was created and selected automatically. Proceeding to Edit Function. This new function is NOT deployed.",
        },
      ]);
      setAssistantPrompt(state.userPrompt);
      setPendingAutoAction({ type: "edit-function" });
      navigate(location.pathname + location.search, { replace: true, state: undefined });
    } else if (state.autoGenerateFunction && state.userPrompt) {
      if (state.previousEnvironmentId) {
        setAutoCreatedFromReadOnly({ previousEnvironmentId: state.previousEnvironmentId });
      }
      setAssistantMessages((prev) => [
        ...prev,
        {
          type: "system",
          content:
            "The selected function was deployed and cannot be edited directly. A new version was created and selected automatically. Proceeding to Generate Function. This new function is NOT deployed.",
        },
      ]);
      setGenerateFunctionPrompt(state.userPrompt);
      setPendingAutoAction({ type: "generate-function" });
      navigate(location.pathname + location.search, { replace: true, state: undefined });
    }
  }, [location.state]);

  // Show deploy dialog after user accepts changes (handled in handleAcceptGeneration)

  const currentAgentId = getAgentIdFromPath();
  const previousEnv = autoCreatedFromReadOnly?.previousEnvironmentId
    ? (agentEnvironments as any[]).find((e) => e.id === autoCreatedFromReadOnly?.previousEnvironmentId)
    : null;
  const debugEnv = (agentEnvironments as any[]).find((e) => e.isDebug);

  const handleAutoDeployToPrevious = async () => {
    try {
      if (!previousEnv || !functionId || !currentAgentId) return;
      await dispatch(agentEnvironmentActions.updateById({ id: previousEnv.id, functionId: functionId, agentId: currentAgentId }) as any);
    } finally {
      setShowAutoDeployDialog(false);
      setAutoCreatedFromReadOnly(null);
    }
  };

  const handleAutoDeployToDebug = async () => {
    try {
      if (!debugEnv || !functionId || !currentAgentId) return;
      await dispatch(agentEnvironmentActions.updateById({ id: debugEnv.id, functionId: functionId, agentId: currentAgentId }) as any);
    } finally {
      setShowAutoDeployDialog(false);
      setAutoCreatedFromReadOnly(null);
    }
  };

  const handleAutoDeployCancel = () => {
    setShowAutoDeployDialog(false);
    setAutoCreatedFromReadOnly(null);
  };

  // Defer auto-trigger until editor is ready
  useEffect(() => {
    if (!pendingAutoAction) return;
    if (isAssistantGenerating) return;
    const hasFiles = Object.keys(files).length > 0;
    const hasOpenTab = openTabs.length > 0 || !!activeFile;
    const isGenerate = pendingAutoAction.type === "generate-function";
    if (!isGenerate && (!hasFiles || !hasOpenTab)) return;

    const run = async () => {
      const { type } = pendingAutoAction;
      if (type === "generate-tests") {
        await handleStreamingTestGeneration();
      } else if (type === "generate-outcomes") {
        await handleGenerateOutcomes();
      } else if (type === "edit-function") {
        await handleEditFunction();
      } else if (type === "generate-function") {
        await handleGenerateFunction();
      }
      setPendingAutoAction(null);
    };
    run();
  }, [pendingAutoAction, isAssistantGenerating, files, openTabs, activeFile]);

  // Apply diff decorations when active file changes
  useEffect(() => {
    // Use a timeout to ensure the editor content has been updated
    const timeoutId = setTimeout(() => {
      if (assistantGenerationResult?.visible && activeFile) {
        if (assistantGenerationResult.changedFiles.includes(activeFile)) {
          const originalContent = assistantGenerationResult.originalFiles[activeFile];
          const newContent = assistantGenerationResult.newFiles[activeFile];

          if (newContent) {
            // For new files (empty original content), ensure we compare against empty string
            const effectiveOriginalContent = originalContent || "";
            applyDiffDecorations(effectiveOriginalContent, newContent);
          } else if (originalContent) {
            // For deleted files (empty new content), compare original content against empty string
            applyDiffDecorations(originalContent, "");
          }
        } else {
          // Clear decorations if current file is not in changed files
          if (editorRef.current) {
            const model = editorRef.current.getModel();
            if (model) {
              // Clear stored decorator IDs
              if (decoratorIds.length > 0) {
                model.deltaDecorations(decoratorIds, []);
                setDecoratorIds([]);
              }
              // Also clear any other decorations that might exist
              model.deltaDecorations([], []);
            }
          }
        }
      } else if (!assistantGenerationResult?.visible) {
        // Clear decorations when test generation result is hidden
        if (editorRef.current) {
          const model = editorRef.current.getModel();
          if (model) {
            // Clear stored decorator IDs
            if (decoratorIds.length > 0) {
              model.deltaDecorations(decoratorIds, []);
              setDecoratorIds([]);
            }
            // Also clear any other decorations that might exist
            model.deltaDecorations([], []);
          }
        }
      }
    }, 100); // Small delay to ensure editor content is updated

    return () => clearTimeout(timeoutId);
  }, [
    activeFile,
    assistantGenerationResult?.visible,
    assistantGenerationResult?.changedFiles,
    assistantGenerationResult?.originalFiles,
    assistantGenerationResult?.newFiles,
  ]);

  // Utility functions
  const getLanguageFromExtension = (filename: string): string => {
    const ext = filename.split(".").pop()?.toLowerCase();
    const languageMap: Record<string, string> = {
      py: "python",
      js: "javascript",
      ts: "typescript",
      jsx: "javascript",
      tsx: "typescript",
      json: "json",
      yml: "yaml",
      yaml: "yaml",
      html: "html",
      css: "css",
      md: "markdown",
      sql: "sql",
      xml: "xml",
    };
    return languageMap[ext || ""] || "plaintext";
  };

  const handleFileChange = useCallback(
    async (value: string | undefined) => {
      if (!activeFile) return;

      const content = value || "";
      const updatedFiles = {
        ...files,
        [activeFile]: {
          ...files[activeFile],
          content,
        },
      };

      setFiles(updatedFiles);
      onChange(updatedFiles);

      // Update Redux store with zipped code for backend compatibility
      try {
        const zipBlob = await zipFiles(updatedFiles);
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          const base64Data = base64.split(",")[1];
          const updatedFormData = {
            ...agentFunctionFormData,
            code: base64Data,
            codePath: true,
            isMultifile: true,
            activeFile: propActiveFile || activeFile,
          };
          dispatch(featureActions.setAgentFunctionFormData(updatedFormData));
        };
        reader.readAsDataURL(zipBlob);
      } catch (error) {
        console.error("Error zipping files:", error);
      }
    },
    [files, activeFile, onChange, agentFunctionFormData, dispatch, propActiveFile],
  );

  const getParentFolder = (filePath: string): string | null => {
    const parts = filePath.split("/");
    if (parts.length <= 1) return null; // File is at root level
    return parts.slice(0, -1).join("/");
  };

  // Add function to handle project upload
  const handleProjectUpload = async (files: FileList) => {
    if (files.length === 0) return;

    setUploadedProject(files);
  };

  const uploadProject = async () => {
    if (!uploadedProject || uploadedProject.length === 0) return;

    try {
      const newFiles: FilesState = {};

      // Process each file in the uploaded project
      for (let i = 0; i < uploadedProject.length; i++) {
        const file = uploadedProject[i];

        // Skip directories (they'll be handled by file paths)
        if (file.webkitRelativePath) {
          const filePath = file.webkitRelativePath;

          // Check if file should be ignored
          if (shouldIgnoreFile(filePath)) {
            continue;
          }

          // Strip the root folder name from the path
          // webkitRelativePath format: "folderName/file1.txt" or "folderName/subfolder/file2.txt"
          const pathParts = filePath.split("/");
          if (pathParts.length > 1) {
            // Remove the first part (root folder name) and join the rest
            const relativePath = pathParts.slice(1).join("/");

            // Check if the relative path should also be ignored
            if (shouldIgnoreFile(relativePath)) {
              continue;
            }

            // Read file content
            const content = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onload = (e) => resolve(e.target?.result as string);
              reader.readAsText(file);
            });

            const fileLanguage = getLanguageFromExtension(relativePath);
            newFiles[relativePath] = { content, language: fileLanguage };
          }
        }
      }

      // Replace all files with the new project
      await replaceAllFiles(newFiles);

      // Close modal and reset state
      setShowProjectUploadModal(false);
      setUploadedProject(null);
    } catch (error) {
      console.error("Error uploading project:", error);
      alert("Error uploading project. Please try again.");
    }
  };

  const handleGenerateFunction = async () => {
    if (!generateFunctionPrompt.trim() || !functionId || isAssistantGenerating) return;

    if (readOnly) {
      const agentId = getAgentIdFromPath();
      if (!agentId) return;
      try {
        const newFunction = await dispatch(
          agentFunctionActions.create({
            agentId,
            code: agentFunctionFormData.code,
            language: agentFunctionFormData.language || language,
            name: agentFunctionFormData.name,
            isMultifile: (agentFunctionFormData as any).isMultifile,
          }) as any,
        ).unwrap();
        const previousEnvironment = (agentEnvironments as any[]).find((e) => e.functionId === functionId);
        navigate(`/agents/${agentId}/function?f=${newFunction.id}`, {
          replace: false,
          state: { autoGenerateFunction: true, userPrompt: generateFunctionPrompt, previousEnvironmentId: previousEnvironment?.id },
        });
      } catch (e) {
        console.error("Failed to create new function version:", e);
      }
      return;
    }

    setIsGeneratingFunction(true);

    try {
      await handleAssistantStreamingRequest(
        "generate-function",
        {
          userPrompt: generateFunctionPrompt,
        },
        setIsGeneratingFunction,
        async (zipData, cost, duration) => {
          // Unzip the generated function
          const unzippedFiles = await unzipCode(zipData);
          const newFiles: FilesState = {};

          // Extract all files from the zip
          for (const [filename, content] of Object.entries(unzippedFiles)) {
            const fileLanguage = getLanguageFromExtension(filename);
            newFiles[filename] = { content: content as string, language: fileLanguage };
          }

          // Replace all files with the generated function
          await replaceAllFiles(newFiles);

          // Close modal and reset state
          setShowGenerateFunctionModal(false);
          setGenerateFunctionPrompt("");
        },
      );
    } catch (error) {
      console.error("Error generating function:", error);
      alert("Error generating function. Please try again.");
    } finally {
      setIsGeneratingFunction(false);
    }
  };

  const handleAssistantPrompt = async () => {
    if (!assistantPrompt.trim()) return;
    await handleEditFunction();
  };

  const handleAddOutcomes = async () => {
    await handleGenerateOutcomes();
  };

  // Function to download project as zip
  const downloadProject = async () => {
    try {
      const zipBlob = await zipFiles(files);
      // Create download link
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      // Set the download filename to <function_name>_MM_DD_YYYY.zip
      const today = new Date();
      const pad = (n: number) => n.toString().padStart(2, "0");
      const mm = pad(today.getMonth() + 1);
      const dd = pad(today.getDate());
      const yyyy = today.getFullYear();
      const functionName = agentFunctionFormData?.name || "project";
      link.download = `${functionName}_${mm}_${dd}_${yyyy}.zip`;
      // Trigger download
      document.body.appendChild(link);
      link.click();
      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading project:", error);
      alert("Error downloading project. Please try again.");
    }
  };

  const clearAllDecorations = () => {
    if (editorRef.current) {
      const model = editorRef.current.getModel();
      if (model) {
        // Clear stored decorator IDs
        if (decoratorIds.length > 0) {
          model.deltaDecorations(decoratorIds, []);
          setDecoratorIds([]);
        }
        // Also clear any other decorations that might exist
        model.deltaDecorations([], []);
      }
    }
  };

  const handleAcceptGeneration = async () => {
    if (assistantGenerationResult) {
      // Apply all the new files and remove deleted files
      const updatedFiles = { ...files };

      for (const changedFile of assistantGenerationResult.changedFiles) {
        if (assistantGenerationResult.newFiles[changedFile]) {
          // This is a new or modified file
          updatedFiles[changedFile] = {
            ...files[changedFile],
            content: assistantGenerationResult.newFiles[changedFile],
            language: getLanguageFromExtension(changedFile),
          };
        } else if (assistantGenerationResult.originalFiles[changedFile] && assistantGenerationResult.originalFiles[changedFile] !== "") {
          // This is a deleted file - remove it completely
          delete updatedFiles[changedFile];
        }
      }

      setFiles(updatedFiles);
      onChange(updatedFiles);

      // Handle active file if it was deleted
      let newActiveFile = activeFile;
      if (activeFile && !updatedFiles[activeFile]) {
        // The active file was deleted, switch to another file
        const remainingFiles = Object.keys(updatedFiles).sort();
        if (remainingFiles.length > 0) {
          newActiveFile = remainingFiles[0];
          onActiveFileChange?.(newActiveFile);
        } else {
          // No files left, clear active file
          onActiveFileChange?.("");
        }
      }

      // Remove closed tabs for deleted files
      setOpenTabs((prev) => prev.filter((tab) => updatedFiles[tab]));

      // Update Redux store with the new zip data from the API response
      try {
        const updatedFormData = {
          ...agentFunctionFormData,
          code: assistantGenerationResult.zipData || agentFunctionFormData.code,
          codePath: true,
          isMultifile: true,
          activeFile: newActiveFile,
        };
        dispatch(featureActions.setAgentFunctionFormData(updatedFormData));
      } catch (error) {
        console.error("Error updating Redux store after accepting generation:", error);
      }

      // Clear any decorations
      clearAllDecorations();

      // Remove accepted files from diverged files set since they now match the backend
      const newDivergedFiles = new Set(divergedFiles);
      for (const changedFile of assistantGenerationResult.changedFiles) {
        newDivergedFiles.delete(changedFile);
      }
      setDivergedFiles(newDivergedFiles);

      // Hide the notification bar
      setAssistantGenerationResult(null);

      // If this function was auto-created from a read-only one, now prompt to deploy
      if (autoCreatedFromReadOnly) {
        setShowAutoDeployDialog(true);
      }
    }
  };

  const handleRejectGeneration = async () => {
    if (assistantGenerationResult) {
      // Restore original files and remove new files
      const updatedFiles = { ...files };

      for (const changedFile of assistantGenerationResult.changedFiles) {
        if (assistantGenerationResult.originalFiles[changedFile] === "") {
          // This was a new file - remove it completely
          delete updatedFiles[changedFile];
        } else {
          // This was an existing file - restore original content
          updatedFiles[changedFile] = {
            ...files[changedFile],
            content: assistantGenerationResult.originalFiles[changedFile],
            language: getLanguageFromExtension(changedFile),
          };
        }
      }

      setFiles(updatedFiles);
      onChange(updatedFiles);

      // Handle active file if it was a new file that's being removed
      let newActiveFile = activeFile;
      if (activeFile && !updatedFiles[activeFile]) {
        // The active file was removed, switch to another file
        const remainingFiles = Object.keys(updatedFiles).sort();
        if (remainingFiles.length > 0) {
          newActiveFile = remainingFiles[0];
          onActiveFileChange?.(newActiveFile);
        } else {
          // No files left, clear active file
          onActiveFileChange?.("");
        }
      }

      // Remove closed tabs for deleted files
      setOpenTabs((prev) => prev.filter((tab) => updatedFiles[tab]));

      // Clear any decorations
      clearAllDecorations();

      // Add rejected files to diverged files set since they now differ from backend
      const newDivergedFiles = new Set(divergedFiles);
      for (const changedFile of assistantGenerationResult.changedFiles) {
        newDivergedFiles.add(changedFile);
      }
      setDivergedFiles(newDivergedFiles);

      // Set rejection flag to reset conversation tracking on next request
      setHasRejectedChanges(true);

      // Hide the notification bar
      setAssistantGenerationResult(null);
    }
  };

  // Execute any pending Assistant accept/reject action triggered by navigation dialogs
  useEffect(() => {
    const runPending = async () => {
      if (!pendingAssistantAction) return;
      if (pendingAssistantAction === "accept") {
        await handleAcceptGeneration();
      } else if (pendingAssistantAction === "reject") {
        await handleRejectGeneration();
      }
      // Clear the action so it doesn't repeat and bump a token others can observe
      dispatch(featureActions.setAgentFunctionPendingAssistantAction(null as any));
      dispatch(featureActions.bumpAgentFunctionAssistantActionToken());
    };
    runPending();
    // Only react to the action value
  }, [pendingAssistantAction]);

  // Function to compute diff and apply decorations
  const applyDiffDecorations = (originalContent: string, newContent: string) => {
    if (!editorRef.current) return;

    const model = editorRef.current.getModel();
    if (!model) return;

    // Clear any existing decorations before applying new ones
    if (decoratorIds.length > 0) {
      model.deltaDecorations(decoratorIds, []);
      setDecoratorIds([]);
    }

    // Normalize content to handle trailing newlines consistently
    const normalizedOriginal = originalContent.replace(/\r\n/g, "\n");
    const normalizedNew = newContent.replace(/\r\n/g, "\n");

    // Check if this is a new file (original content is empty) or deleted file (new content is empty but original has content)
    const isNewFile = normalizedOriginal.trim() === "";
    const isDeletedFile = normalizedNew.trim() === "" && normalizedOriginal.trim() !== "";

    if (isNewFile) {
      // For new files, show all lines as "new" - no diff algorithm needed
      const lines = normalizedNew.split("\n");
      const decorations: monaco.editor.IModelDeltaDecoration[] = [];

      // Apply green highlighting to all lines
      for (let i = 0; i < lines.length; i++) {
        const range = new monaco.Range(i + 1, 1, i + 1, 1);
        decorations.push({
          range,
          options: {
            isWholeLine: true,
            className: "diff-added-line",
            beforeContentClassName: "diff-added-marker",
          },
        });
      }

      // Set the content first, then apply decorations
      model.setValue(normalizedNew);

      // Small delay to ensure content is set, then apply decorations
      setTimeout(() => {
        const newDecoratorIds = model.deltaDecorations([], decorations);
        setDecoratorIds(newDecoratorIds);
      }, 10);
    } else if (isDeletedFile) {
      // For deleted files, show all lines as "removed" - no diff algorithm needed
      const lines = normalizedOriginal.split("\n");
      const decorations: monaco.editor.IModelDeltaDecoration[] = [];

      // Apply red highlighting to all lines
      for (let i = 0; i < lines.length; i++) {
        const range = new monaco.Range(i + 1, 1, i + 1, 1);
        decorations.push({
          range,
          options: {
            isWholeLine: true,
            className: "diff-removed-line",
            beforeContentClassName: "diff-removed-marker",
          },
        });
      }

      // Set the content first, then apply decorations
      model.setValue(normalizedOriginal);

      // Small delay to ensure content is set, then apply decorations
      setTimeout(() => {
        const newDecoratorIds = model.deltaDecorations([], decorations);
        setDecoratorIds(newDecoratorIds);
      }, 10);
    } else {
      // For existing files, use the diff library to compute the differences
      const changes = Diff.diffLines(normalizedOriginal, normalizedNew);
      const decorations: monaco.editor.IModelDeltaDecoration[] = [];

      // Create a merged view that shows both old and new lines
      let mergedContent = "";
      let currentLine = 1;

      changes.forEach((change: Diff.Change) => {
        if (change.added) {
          // Add new lines with green highlighting
          mergedContent += change.value;
          const lines = change.value.split("\n");
          // Count lines properly, handling the last line correctly
          const lineCount = lines.length > 1 ? lines.length - 1 : 1;
          for (let i = 0; i < lineCount; i++) {
            const range = new monaco.Range(currentLine + i, 1, currentLine + i, 1);
            decorations.push({
              range,
              options: {
                isWholeLine: true,
                className: "diff-added-line",
                beforeContentClassName: "diff-added-marker",
              },
            });
          }
          currentLine += lineCount;
        } else if (change.removed) {
          // Add removed lines with red highlighting
          mergedContent += change.value;
          const lines = change.value.split("\n");
          // Count lines properly, handling the last line correctly
          const lineCount = lines.length > 1 ? lines.length - 1 : 1;
          for (let i = 0; i < lineCount; i++) {
            const range = new monaco.Range(currentLine + i, 1, currentLine + i, 1);
            decorations.push({
              range,
              options: {
                isWholeLine: true,
                className: "diff-removed-line",
                beforeContentClassName: "diff-removed-marker",
              },
            });
          }
          currentLine += lineCount;
        } else {
          // Add unchanged lines
          mergedContent += change.value;
          currentLine += (change.value.match(/\n/g) || []).length;
        }
      });

      // Update the editor content to show the merged view
      model.setValue(mergedContent);

      // Apply decorations
      const newDecoratorIds = model.deltaDecorations([], decorations);
      setDecoratorIds(newDecoratorIds);
    }
  };

  const handleFileUpload = async (files: FileList) => {
    if (files.length === 0) return;

    const file = files[0];
    setUploadedFile(file);

    // Auto-fill the filename with the uploaded file's name
    const fileName = file.name;
    setModalFileName(fileName);
  };

  const addNewFile = async (filename?: string, fileContent?: string) => {
    const fileNameToUse = filename || modalFileName;
    if (!fileNameToUse) return;

    // Determine the path for the new file using activeFolder
    const targetPath = activeFolder || "";
    const fullPath = targetPath ? `${targetPath}/${fileNameToUse}` : fileNameToUse;

    if (files[fullPath]) {
      setAddFileError("A file with that name already exists in this location.");
      return;
    }
    setAddFileError("");

    const fileLanguage = getLanguageFromExtension(fullPath);
    const updatedFiles = {
      ...files,
      [fullPath]: {
        content: fileContent || "",
        language: fileLanguage,
      },
    };
    setFiles(updatedFiles);
    onChange(updatedFiles);

    // Open new file in a tab
    setOpenTabs((prev) => [...prev, fullPath]);
    onActiveFileChange?.(fullPath);

    // If adding to a folder, ensure it's expanded
    if (targetPath) {
      setExpandedFolders((prev) => {
        const newSet = new Set(prev);
        newSet.add(targetPath);
        return newSet;
      });
    }

    // Update Redux store with zipped code
    try {
      const zipBlob = await zipFiles(updatedFiles);
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        const base64Data = base64.split(",")[1];
        const updatedFormData = {
          ...agentFunctionFormData,
          code: base64Data,
          codePath: true,
          isMultifile: true,
          activeFile: fullPath,
        };
        dispatch(featureActions.setAgentFunctionFormData(updatedFormData));
      };
      reader.readAsDataURL(zipBlob);
    } catch (error) {
      console.error("Error zipping files after add:", error);
    }

    setModalFileName("");
    setUploadedFile(null);
    setShowAddFileModal(false);
    setAddFileError("");
  };

  const removeFile = async (filename: string) => {
    if (Object.keys(files).length > 1) {
      const newFiles = { ...files };
      delete newFiles[filename];
      setFiles(newFiles);
      onChange(newFiles);

      // Also remove from open tabs
      setOpenTabs((prev) => prev.filter((tab) => tab !== filename));

      let newActiveFile = activeFile;
      if (activeFile === filename) {
        const sortedNewFiles = Object.keys(newFiles).sort();
        newActiveFile = sortedNewFiles[0];
        onActiveFileChange?.(newActiveFile);
      }

      // Update Redux store with zipped code for backend compatibility
      try {
        const zipBlob = await zipFiles(newFiles);
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          const base64Data = base64.split(",")[1];
          const updatedFormData = {
            ...agentFunctionFormData,
            code: base64Data,
            codePath: true,
            isMultifile: true,
            activeFile: newActiveFile,
          };
          dispatch(featureActions.setAgentFunctionFormData(updatedFormData));
        };
        reader.readAsDataURL(zipBlob);
      } catch (error) {
        console.error("Error zipping files after delete:", error);
      }
    }
  };

  const closeTab = (filename: string) => {
    // Close tab without deleting file
    const tabIndex = openTabs.indexOf(filename);
    const newOpenTabs = openTabs.filter((tab) => tab !== filename);
    setOpenTabs(newOpenTabs);

    // If closing the active tab, switch to a tab immediately next to it (like VSCode)
    if (activeFile === filename && newOpenTabs.length > 0) {
      // Prefer the tab to the right, otherwise the one to the left
      let newActiveFile: string | undefined;
      if (tabIndex < newOpenTabs.length) {
        newActiveFile = newOpenTabs[tabIndex]; // right neighbor
      } else if (tabIndex - 1 >= 0) {
        newActiveFile = newOpenTabs[tabIndex - 1]; // left neighbor
      } else {
        newActiveFile = newOpenTabs[0]; // fallback
      }
      onActiveFileChange?.(newActiveFile);
    } else if (newOpenTabs.length === 0) {
      // No tabs left, clear active file
      onActiveFileChange?.("");
    }
  };

  const handleActiveFileChange = (filename: string, fromTree: boolean = false) => {
    let updatedOpenTabs = [...openTabs];
    const currentActiveIndex = updatedOpenTabs.indexOf(activeFile);
    const isAlreadyOpen = updatedOpenTabs.includes(filename);

    if (fromTree && filename !== activeFile) {
      let insertIndex = currentActiveIndex >= 0 ? currentActiveIndex + 1 : updatedOpenTabs.length;

      if (isAlreadyOpen) {
        // Remove from current position
        updatedOpenTabs = updatedOpenTabs.filter((tab) => tab !== filename);
      }

      // Insert at the calculated position
      updatedOpenTabs.splice(insertIndex, 0, filename);
      setOpenTabs(updatedOpenTabs);
    } else if (!isAlreadyOpen) {
      // For non-tree selections or new files, just add to end if needed
      updatedOpenTabs.push(filename);
      setOpenTabs(updatedOpenTabs);
    }

    // Always update active file
    onActiveFileChange?.(filename);
  };

  const showContextMenu = (e: React.MouseEvent, filename: string) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      targetFile: filename,
    });
  };

  // Helper to check if a path is a folder (has children)
  const isFolderPath = (path: string) => Object.keys(files).some((f) => f.startsWith(`${path}/`));

  // Remove a folder and all its contents
  const removeFolder = async (folderPath: string) => {
    // Remove all files under the folder
    const newFiles = { ...files };
    const toDelete = Object.keys(newFiles).filter((f) => f === folderPath || f.startsWith(`${folderPath}/`));
    toDelete.forEach((f) => delete newFiles[f]);
    setFiles(newFiles);
    onChange(newFiles);

    // Remove from open tabs
    setOpenTabs((prev) => prev.filter((tab) => !toDelete.includes(tab)));

    // Update active file if needed
    let newActiveFile = activeFile;
    if (activeFile && toDelete.includes(activeFile)) {
      const sortedNewFiles = Object.keys(newFiles).sort();
      newActiveFile = sortedNewFiles[0] || "";
      onActiveFileChange?.(newActiveFile);
    }

    // Update Redux store with zipped code for backend compatibility
    try {
      const zipBlob = await zipFiles(newFiles);
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        const base64Data = base64.split(",")[1];
        const updatedFormData = {
          ...agentFunctionFormData,
          code: base64Data,
          codePath: true,
          isMultifile: true,
          activeFile: newActiveFile,
        };
        dispatch(featureActions.setAgentFunctionFormData(updatedFormData));
      };
      reader.readAsDataURL(zipBlob);
    } catch (error) {
      console.error("Error zipping files after folder delete:", error);
    }
  };

  const hideContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, targetFile: "" });
  };

  const handleDeleteFile = () => {
    const handlerFileName = `handler.${language === "python" ? "py" : "js"}`;
    if (contextMenu.targetFile === handlerFileName) {
      // Don't allow deletion of handler file
      return;
    }
    if (contextMenu.targetFile) {
      if (isFolderPath(contextMenu.targetFile)) {
        setFolderToDelete(contextMenu.targetFile);
        setShowDeleteFolderDialog(true);
      } else {
        removeFile(contextMenu.targetFile);
      }
    }
    hideContextMenu();
  };

  const handleRenameFile = () => {
    if (contextMenu.targetFile) {
      setRenamingFile(contextMenu.targetFile);
      setRenameValue(contextMenu.targetFile.split("/").pop() || contextMenu.targetFile);
      setRenameError("");
    }
    hideContextMenu();
  };

  const handleRenameConfirm = async () => {
    if (renamingFile && renameValue && renameValue !== renamingFile) {
      // Only allow renaming for files
      if (Object.keys(files).some((f) => f.startsWith(`${renamingFile}/`))) {
        setRenamingFile("");
        setRenameValue("");
        setRenameError("");
        return;
      }
      // Prevent renaming to an existing file at the same level
      const parentPath = renamingFile.includes("/") ? renamingFile.slice(0, renamingFile.lastIndexOf("/")) : "";
      const newPath = parentPath ? `${parentPath}/${renameValue}` : renameValue;
      const siblingPaths = Object.keys(files).filter((f) => {
        const siblingParent = f.includes("/") ? f.slice(0, f.lastIndexOf("/")) : "";
        const siblingName = f.includes("/") ? f.slice(f.lastIndexOf("/") + 1) : f;
        return siblingParent === parentPath && siblingName === renameValue;
      });
      if ((files[newPath] && newPath !== renamingFile) || siblingPaths.length > 0) {
        setRenameError("A file with that name already exists.");
        return;
      }
      setRenameError("");

      const updatedFiles: FilesState = { ...files };
      updatedFiles[newPath] = updatedFiles[renamingFile];
      delete updatedFiles[renamingFile];
      setFiles(updatedFiles);
      onChange(updatedFiles);

      // Update open tabs
      setOpenTabs((prev) => prev.map((tab) => (tab === renamingFile ? newPath : tab)));

      // Update active file if it was the renamed file
      if (activeFile === renamingFile) {
        onActiveFileChange?.(newPath);
      } else if (!activeFile || !updatedFiles[activeFile]) {
        onActiveFileChange?.(newPath);
      }

      // Update Redux store with zipped code for backend compatibility
      try {
        const zipBlob = await zipFiles(updatedFiles);
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          const base64Data = base64.split(",")[1];
          const updatedFormData = {
            ...agentFunctionFormData,
            code: base64Data,
            codePath: true,
            isMultifile: true,
            activeFile: activeFile === renamingFile ? newPath : activeFile,
          };
          dispatch(featureActions.setAgentFunctionFormData(updatedFormData));
        };
        reader.readAsDataURL(zipBlob);
      } catch (error) {
        console.error("Error zipping files after rename:", error);
      }
      setRenamingFile("");
      setRenameValue("");
      setRenameError("");
    }
    setRenamingFile("");
    setRenameValue("");
    setRenameError("");
  };

  const handleRenameCancel = () => {
    setRenamingFile("");
    setRenameValue("");
    setRenameError("");
  };

  const moveNode = async (sourcePath: string, targetPath: string) => {
    if (sourcePath === targetPath) return;
    if (targetPath.startsWith(`${sourcePath}/`)) {
      return;
    }

    const updatedFiles = { ...files };
    const affectedPaths = Object.keys(updatedFiles).filter((p) => p.startsWith(`${sourcePath}/`) || p === sourcePath);

    const sourceName = sourcePath.split("/").pop() || sourcePath;
    const pathMap = new Map<string, string>();
    affectedPaths.forEach((oldPath) => {
      const relative = oldPath.substring(sourcePath.length);
      const newPath = targetPath ? `${targetPath}/${sourceName}${relative}` : `${sourceName}${relative}`;
      pathMap.set(oldPath, newPath);
    });

    pathMap.forEach((newP, oldP) => {
      updatedFiles[newP] = updatedFiles[oldP];
      delete updatedFiles[oldP];
    });

    setFiles(updatedFiles);
    onChange(updatedFiles);

    // Update open tabs
    setOpenTabs((prev) => prev.map((tab) => pathMap.get(tab) || tab));

    // Update active file
    let newActiveFile = activeFile;
    if (activeFile && pathMap.has(activeFile)) {
      newActiveFile = pathMap.get(activeFile)!;
      onActiveFileChange?.(newActiveFile);
    }

    // Update expanded folders
    setExpandedFolders((prev) => {
      const newSet = new Set<string>();
      prev.forEach((oldExp) => {
        if (pathMap.has(oldExp)) {
          newSet.add(pathMap.get(oldExp)!);
        } else if (!affectedPaths.includes(oldExp)) {
          newSet.add(oldExp);
        }
      });
      return newSet;
    });

    // Update selected, focused, activeFolder
    const updateStatePath = (state: string) => pathMap.get(state) || state;
    setSelectedNode(updateStatePath(selectedNode));
    setFocusedNode(updateStatePath(focusedNode));
    if (activeFolder !== null) {
      setActiveFolder(pathMap.get(activeFolder) ?? activeFolder);
    }

    // Zip and update Redux
    try {
      const zipBlob = await zipFiles(updatedFiles);
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        const base64Data = base64.split(",")[1];
        const updatedFormData = {
          ...agentFunctionFormData,
          code: base64Data,
          codePath: true,
          isMultifile: true,
          activeFile: newActiveFile,
        };
        dispatch(featureActions.setAgentFunctionFormData(updatedFormData));
      };
      reader.readAsDataURL(zipBlob);
    } catch (error) {
      console.error("Error zipping after move:", error);
    }
  };

  // Monaco editor setup with advanced features
  const handleEditorDidMount = (editor: monaco.editor.IStandaloneCodeEditor, monacoInstance: typeof monaco) => {
    editorRef.current = editor;

    // Set up Python language support
    monacoInstance.languages.setMonarchTokensProvider("python", python);

    // Custom autocomplete for Python
    monacoInstance.languages.registerCompletionItemProvider("python", {
      provideCompletionItems: (model: monaco.editor.ITextModel, position: monaco.Position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        const suggestions = [
          {
            label: "print",
            kind: monacoInstance.languages.CompletionItemKind.Function,
            insertText: "print(${1:value})",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range,
            documentation: "Print values to the console",
          },
          {
            label: "def",
            kind: monacoInstance.languages.CompletionItemKind.Keyword,
            insertText: "def ${1:function_name}(${2:parameters}):\n\t${3:pass}",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range,
            documentation: "Define a function",
          },
          {
            label: "class",
            kind: monacoInstance.languages.CompletionItemKind.Class,
            insertText: "class ${1:ClassName}:\n\t${2:pass}",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range,
            documentation: "Define a class",
          },
          {
            label: "async def",
            kind: monacoInstance.languages.CompletionItemKind.Keyword,
            insertText: "async def ${1:function_name}(${2:parameters}):\n\t${3:pass}",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range,
            documentation: "Define an async function",
          },
        ];
        return { suggestions };
      },
    });

    // Custom autocomplete for JavaScript
    monacoInstance.languages.registerCompletionItemProvider("javascript", {
      provideCompletionItems: (model: monaco.editor.ITextModel, position: monaco.Position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        const suggestions = [
          {
            label: "console.log",
            kind: monacoInstance.languages.CompletionItemKind.Function,
            insertText: "console.log(${1:value});",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range,
            documentation: "Log values to the console",
          },
          {
            label: "function",
            kind: monacoInstance.languages.CompletionItemKind.Keyword,
            insertText: "function ${1:functionName}(${2:parameters}) {\n\t${3:// code}\n}",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range,
            documentation: "Define a function",
          },
          {
            label: "async function",
            kind: monacoInstance.languages.CompletionItemKind.Keyword,
            insertText: "async function ${1:functionName}(${2:parameters}) {\n\t${3:// code}\n}",
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range,
            documentation: "Define an async function",
          },
        ];
        return { suggestions };
      },
    });

    // Add custom keybindings (excluding Ctrl+S which is handled by the layout)
    editor.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyF, () => {
      const findAction = editor.getAction("actions.find");
      if (findAction) {
        findAction.run();
      }
    });

    editor.addCommand(monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyMod.Shift | monacoInstance.KeyCode.KeyF, () => {
      const findReplaceAction = editor.getAction("editor.action.startFindReplaceAction");
      if (findReplaceAction) {
        findReplaceAction.run();
      }
    });
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) {
        hideContextMenu();
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [contextMenu.visible]);

  // Tree structure for files and folders
  interface TreeNode {
    name: string;
    type: "file" | "folder";
    path: string;
    children?: TreeNode[];
    isExpanded?: boolean;
  }

  // Search result structure
  interface SearchResult {
    filePath: string;
    fileName: string;
    lineNumber: number;
    lineContent: string;
    matchStart: number;
    matchEnd: number;
    contextBefore?: string;
    contextAfter?: string;
  }

  // Alphabetical sorting: hidden files/dirs first, then folders before files, then alphabetically
  const sortTreeNodes = (nodes: TreeNode[]): TreeNode[] => {
    return nodes.sort((a, b) => {
      const aHidden = a.name.startsWith(".");
      const bHidden = b.name.startsWith(".");

      // Hidden items come first
      if (aHidden !== bHidden) return aHidden ? -1 : 1;

      // Then sort by type (folders before files)
      if (a.type !== b.type) {
        if (a.type === "folder" && b.type === "file") return -1;
        if (a.type === "file" && b.type === "folder") return 1;
      }

      // Then sort alphabetically by name (case-insensitive)
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
  };

  const buildFileTree = (fileNames: string[]): TreeNode[] => {
    const tree: TreeNode[] = [];
    const nodeMap = new Map<string, TreeNode>();

    // Create root node
    const root: TreeNode = { name: "", type: "folder", path: "", children: [] };
    nodeMap.set("", root);

    // Process each file path
    fileNames.forEach((filePath) => {
      const parts = filePath.split("/");
      let currentPath = "";
      let currentNode = root;

      // Process each part of the path
      parts.forEach((part, index) => {
        const isLastPart = index === parts.length - 1;
        const newPath = currentPath ? `${currentPath}/${part}` : part;

        if (!nodeMap.has(newPath)) {
          const node: TreeNode = {
            name: part,
            type: isLastPart ? "file" : "folder",
            path: newPath,
            children: isLastPart ? undefined : [],
          };
          nodeMap.set(newPath, node);
          currentNode.children?.push(node);
        }

        currentNode = nodeMap.get(newPath)!;
        currentPath = newPath;
      });
    });

    // Sort all children at every level using VSCode-style sorting
    const sortAllChildren = (node: TreeNode): void => {
      if (node.children) {
        node.children = sortTreeNodes(node.children);
        node.children.forEach(sortAllChildren);
      }
    };

    sortAllChildren(root);

    return root.children || [];
  };

  const toggleFolder = (folderPath: string) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderPath)) {
        newSet.delete(folderPath);
      } else {
        newSet.add(folderPath);
      }
      return newSet;
    });
  };

  // Get flattened list of visible nodes for keyboard navigation
  const getVisibleNodes = (nodes: TreeNode[]): string[] => {
    const visible: string[] = [];

    const traverse = (nodeList: TreeNode[]) => {
      for (const node of nodeList) {
        visible.push(node.path);
        if (node.type === "folder" && expandedFolders.has(node.path) && node.children) {
          traverse(node.children);
        }
      }
    };

    traverse(nodes);
    return visible;
  };

  // Navigate tree with arrow keys
  const navigateTree = (direction: "up" | "down") => {
    const tree = buildFileTree(sortedFileNames);
    const filteredTree = filterTreeBySearch(tree, searchQuery);
    const visibleNodes = getVisibleNodes(filteredTree);

    const currentIndex = visibleNodes.indexOf(focusedNode);
    let newIndex = currentIndex;

    if (direction === "up" && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else if (direction === "down" && currentIndex < visibleNodes.length - 1) {
      newIndex = currentIndex + 1;
    }

    if (newIndex !== currentIndex) {
      const newFocusedNode = visibleNodes[newIndex];
      setFocusedNode(newFocusedNode);
      setSelectedNode(newFocusedNode);

      // Focus the DOM element
      const element = document.querySelector(`[data-tree-node="${newFocusedNode}"]`) as HTMLElement;
      if (element) {
        element.focus();
      }
    }
  };

  const filterTreeBySearch = (nodes: TreeNode[], query: string): TreeNode[] => {
    if (!query.trim()) return nodes;

    const filtered: TreeNode[] = [];

    nodes.forEach((node) => {
      if (node.type === "file") {
        // Include files that match the search query
        if (node.name.toLowerCase().includes(query.toLowerCase())) {
          filtered.push(node);
        }
      } else {
        // For folders, check if any children match
        const filteredChildren = filterTreeBySearch(node.children || [], query);
        if (filteredChildren.length > 0) {
          filtered.push({
            ...node,
            children: filteredChildren,
          });
        }
      }
    });

    // Apply VSCode-style sorting to filtered results
    return sortTreeNodes(filtered);
  };

  // Global content search function with performance optimizations
  const searchFileContents = useCallback(
    (query: string): SearchResult[] => {
      if (!query.trim() || query.length < 2) return []; // Don't search single characters

      const results: SearchResult[] = [];
      const searchRegex = new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");

      Object.entries(files).forEach(([filePath, fileData]) => {
        const lines = fileData.content.split("\n");

        lines.forEach((line, index) => {
          let match;
          const lineContent = line;

          // Reset regex for multiple matches in same line
          searchRegex.lastIndex = 0;

          while ((match = searchRegex.exec(lineContent)) !== null) {
            const trimmedLine = lineContent.trim();
            const trimOffset = lineContent.indexOf(trimmedLine);
            const adjustedMatchStart = Math.max(0, match.index - trimOffset);
            const adjustedMatchEnd = Math.max(0, match.index + match[0].length - trimOffset);

            results.push({
              filePath,
              fileName: filePath.split("/").pop() || filePath,
              lineNumber: index + 1,
              lineContent: trimmedLine,
              matchStart: adjustedMatchStart,
              matchEnd: adjustedMatchEnd,
              contextBefore: index > 0 ? lines[index - 1]?.trim() : undefined,
              contextAfter: index < lines.length - 1 ? lines[index + 1]?.trim() : undefined,
            });

            // Prevent infinite loops with zero-width matches
            if (match.index === searchRegex.lastIndex) {
              searchRegex.lastIndex++;
            }
          }
        });
      });

      return results;
    },
    [files],
  );

  // Debounced search query for better performance
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  useEffect(() => {
    if (searchMode === "content" && searchQuery.trim().length > 0) {
      setIsSearching(true);
    }

    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setIsSearching(false);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery, searchMode]);

  // Handle search mode and results with memoization
  const searchResults = useMemo(() => {
    return searchMode === "content" ? searchFileContents(debouncedSearchQuery) : [];
  }, [searchMode, debouncedSearchQuery, searchFileContents]);

  // Track the last search query to prevent duplicate expansions
  const lastExpandedQuery = useRef<string>("");

  // Update search results visibility (use debounced query for content search)
  React.useEffect(() => {
    if (searchMode === "content") {
      setShowSearchResults(debouncedSearchQuery.trim().length > 0);

      // Auto-expand files when starting a new search (only if query changed)
      if (debouncedSearchQuery.trim().length > 0 && searchResults.length > 0 && lastExpandedQuery.current !== debouncedSearchQuery) {
        const fileKeys = new Set(searchResults.map((result: SearchResult) => result.filePath));
        setExpandedSearchFiles(fileKeys);
        lastExpandedQuery.current = debouncedSearchQuery;
      }
    } else {
      setShowSearchResults(false);
      setExpandedSearchFiles(new Set());
      lastExpandedQuery.current = "";
    }
  }, [searchMode, debouncedSearchQuery, searchResults]);

  // Toggle file expansion in search results
  const toggleSearchFileExpansion = (filePath: string) => {
    setExpandedSearchFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(filePath)) {
        newSet.delete(filePath);
      } else {
        newSet.add(filePath);
      }
      return newSet;
    });
  };

  // Navigate to search result
  const navigateToSearchResult = (result: SearchResult) => {
    // Open the file
    handleActiveFileChange(result.filePath, true);

    // Focus the editor and navigate to line (with a small delay to ensure editor is mounted)
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.revealLineInCenter(result.lineNumber);
        editorRef.current.setPosition({ lineNumber: result.lineNumber, column: result.matchStart + 1 });
        editorRef.current.focus();
      }
    }, 100);
  };

  // Handle sidebar resizing
  const startResize = useCallback(
    (e: React.MouseEvent) => {
      setIsResizing(true);
      e.preventDefault();

      const startX = e.clientX;
      const startWidth = sidebarWidth;

      const handleMouseMove = (e: MouseEvent) => {
        const diff = e.clientX - startX;
        const newWidth = Math.max(150, Math.min(600, startWidth + diff)); // Min 150px, Max 600px
        setSidebarWidth(newWidth);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [sidebarWidth],
  );

  // Enhanced tree node rendering with keyboard navigation
  const renderTreeNode = (node: TreeNode, depth: number = 0): JSX.Element => {
    const isExpanded = expandedFolders.has(node.path);
    const isSelected = selectedNode === node.path;
    const isFocused = focusedNode === node.path;
    const isActive = activeFile === node.path;
    const isActiveFolder = activeFolder === node.path;
    const isInActiveFolder = activeFolder && node.path.startsWith(activeFolder + "/");
    const isActiveFolderNull = activeFolder === null;
    const shouldShowActiveBorder = isActiveFolder || isInActiveFolder || isActiveFolderNull;
    const paddingLeft = depth * 16; // 16px per level

    const handleKeyDown = (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "Enter":
        case " ":
          e.preventDefault();
          if (node.type === "folder") {
            toggleFolder(node.path);
            setActiveFolder(node.path);
          } else {
            handleActiveFileChange(node.path, true);
            setActiveFolder(getParentFolder(node.path));
          }
          break;
        case "ArrowRight":
          e.preventDefault();
          if (node.type === "folder" && !isExpanded) {
            toggleFolder(node.path);
          }
          break;
        case "ArrowLeft":
          e.preventDefault();
          if (node.type === "folder" && isExpanded) {
            toggleFolder(node.path);
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          navigateTree("up");
          break;
        case "ArrowDown":
          e.preventDefault();
          navigateTree("down");
          break;
      }
    };

    const handleClick = () => {
      setSelectedNode(node.path);
      setFocusedNode(node.path);
      if (node.type === "folder") {
        toggleFolder(node.path);
        setActiveFolder(node.path); // Set active folder when folder is clicked
      } else {
        handleActiveFileChange(node.path, true);
        setActiveFolder(getParentFolder(node.path)); // Set active folder to parent of file
      }
    };

    const handleFocus = () => {
      setFocusedNode(node.path);
    };

    if (node.type === "folder") {
      const effectiveExpanded = searchQuery && (node.children?.length ?? 0) > 0 ? true : expandedFolders.has(node.path);

      return (
        <div key={node.path}>
          <div
            draggable={true}
            onDragStart={(e) => {
              e.dataTransfer.setData("text/plain", node.path);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={(e) => {
              e.preventDefault();
              setDropTarget(node.path);
            }}
            onDragLeave={() => setDropTarget(null)}
            onDrop={(e) => {
              e.preventDefault();
              const sourcePath = e.dataTransfer.getData("text/plain");
              moveNode(sourcePath, node.path);
              setDropTarget(null);
            }}
            className={clsx(
              "flex items-center px-3 py-2 cursor-pointer text-sm border-b border-gray-200 dark:border-gray-600 outline-none",
              "hover:bg-gray-100 dark:hover:bg-gray-700",
              isSelected && "bg-brand/10 dark:bg-brand/20",
              dropTarget === node.path && "bg-brand/20 dark:bg-brand/30",
              shouldShowActiveBorder && "border-l-2 !border-l-brand",
              isFocused && "ring-2 ring-brand ring-inset",
            )}
            style={{ paddingLeft: paddingLeft + 12 }}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onContextMenu={(e) => showContextMenu(e, node.path)}
            tabIndex={0}
            role="treeitem"
            aria-expanded={effectiveExpanded}
            aria-label={`${node.name} folder`}
            data-tree-node={node.path}
          >
            {effectiveExpanded ? (
              <Icon
                name="chevron-down"
                size={16}
                className="mr-1 text-gray-500 dark:text-gray-400"
              />
            ) : (
              <Icon
                name="chevron-right"
                size={16}
                className="mr-1 text-gray-500 dark:text-gray-400"
              />
            )}
            {effectiveExpanded ? (
              <Icon
                name="folder_open"
                size={16}
                className="mr-2 text-gray-500 dark:text-gray-400"
              />
            ) : (
              <Icon
                name="folder"
                size={16}
                className="mr-2 text-gray-500 dark:text-gray-400"
              />
            )}
            <span className="flex-1 text-gray-900 dark:text-white font-medium truncate min-w-0">{node.name}</span>
          </div>
          {effectiveExpanded && node.children && (
            <div
              role="group"
              aria-labelledby={`folder-${node.path}`}
            >
              {node.children.map((child) => renderTreeNode(child, depth + 1))}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div
          key={node.path}
          draggable={true}
          onDragStart={(e) => {
            e.dataTransfer.setData("text/plain", node.path);
          }}
          className={clsx(
            "flex items-center px-3 py-2 cursor-pointer text-sm border-b border-gray-200 dark:border-gray-600 outline-none",
            "hover:bg-gray-100 dark:hover:bg-gray-700",
            isActive && "bg-brand/10 dark:bg-brand/20 border-brand/20 dark:border-brand/30",
            isSelected && !isActive && "bg-gray-50 dark:bg-gray-800",
            shouldShowActiveBorder && "border-l-2 !border-l-brand",
            isFocused && "ring-2 ring-brand ring-inset",
          )}
          style={{ paddingLeft: paddingLeft + 12 }}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onContextMenu={(e) => showContextMenu(e, node.path)}
          tabIndex={0}
          role="treeitem"
          aria-label={`${node.name} file`}
          data-tree-node={node.path}
        >
          <div className="flex-shrink-0 mr-2">
            <Icon
              name="file"
              size={16}
              className="text-gray-500 dark:text-gray-400"
            />
          </div>
          {renamingFile === node.path && node.type === "file" ? (
            <div className="flex-1 flex flex-col">
              <input
                type="text"
                value={renameValue}
                onChange={(e) => {
                  setRenameValue(e.target.value);
                  setRenameError("");
                }}
                onKeyDown={(e) => {
                  e.stopPropagation(); // Prevent tree navigation
                  if (e.key === "Enter") {
                    handleRenameConfirm();
                  } else if (e.key === "Escape") {
                    handleRenameCancel();
                  }
                  // Allow arrow keys for cursor movement
                }}
                onBlur={handleRenameCancel}
                onClick={(e) => e.stopPropagation()} // Prevent tree selection
                className="bg-white dark:bg-gray-700 border border-blue-500 rounded px-1 text-gray-900 dark:text-white text-sm"
                style={{
                  width: `calc(175px - 20px - ${depth * 16}px - 16px)` as any,
                  marginLeft: 0,
                  display: "block",
                }}
                autoFocus
              />
              {renameError && <div className="text-xs text-red-600 mt-1">{renameError}</div>}
            </div>
          ) : (
            <span className="flex-1 text-gray-900 dark:text-white truncate min-w-0">{node.name}</span>
          )}
        </div>
      );
    }
  };

  const renderSearchResults = () => {
    if (isSearching) {
      return (
        <div className="flex flex-col items-center justify-center h-32 text-gray-500 dark:text-gray-400">
          <Icon
            name="search"
            size={24}
            className="mb-2 animate-pulse"
          />
          <span className="text-sm">Searching...</span>
        </div>
      );
    }

    if (searchResults.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-32 text-gray-500 dark:text-gray-400">
          <Icon
            name="search"
            size={24}
            className="mb-2"
          />
          <span className="text-sm">{debouncedSearchQuery.trim() ? "No matches found" : "Start typing to search"}</span>
        </div>
      );
    }

    // Group results by file
    const resultsByFile = searchResults.reduce(
      (acc: Record<string, SearchResult[]>, result: SearchResult) => {
        if (!acc[result.filePath]) {
          acc[result.filePath] = [];
        }
        acc[result.filePath].push(result);
        return acc;
      },
      {} as Record<string, SearchResult[]>,
    );

    return (
      <div className="flex-1 flex flex-col">
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-600 text-xs text-gray-600 dark:text-gray-400 flex-shrink-0">
          {searchResults.length} result{searchResults.length !== 1 ? "s" : ""} in {Object.keys(resultsByFile).length} file
          {Object.keys(resultsByFile).length !== 1 ? "s" : ""}
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-1 p-1">
            {Object.entries(resultsByFile).map(([filePath, results]: [string, SearchResult[]]) => (
              <div
                key={filePath}
                className="border-b border-gray-100 dark:border-gray-700 last:border-b-0"
              >
                {/* File header */}
                <div
                  className="sticky top-0 z-10 px-3 py-2 bg-gray-50 dark:bg-gray-800 text-sm font-medium text-gray-900 dark:text-white flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-gray-600"
                  onClick={() => toggleSearchFileExpansion(filePath)}
                >
                  <Icon
                    name={expandedSearchFiles.has(filePath) ? "chevron-down" : "chevron-right"}
                    size={14}
                    className="mr-2 text-gray-500 dark:text-gray-400"
                  />
                  <Icon
                    name="file"
                    size={14}
                    className="mr-2 text-gray-500 dark:text-gray-400"
                  />
                  <span className="truncate">{results[0].fileName}</span>
                  <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
                    {results.length} match{results.length !== 1 ? "es" : ""}
                  </span>
                </div>
                {/* Search results for this file */}
                {expandedSearchFiles.has(filePath) && (
                  <div className="space-y-1">
                    {results.map((result: SearchResult, index: number) => (
                      <div
                        key={`${result.filePath}-${result.lineNumber}-${index}`}
                        className="pl-8 pr-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-l-2 border-transparent hover:border-brand transition-colors"
                        onClick={() => navigateToSearchResult(result)}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-600 dark:text-gray-400">Line {result.lineNumber}</span>
                        </div>
                        <div className="text-sm text-gray-900 dark:text-white">
                          <span className="text-gray-600 dark:text-gray-400">{result.lineContent.substring(0, result.matchStart)}</span>
                          <span className="bg-yellow-200 dark:bg-yellow-600 text-gray-900 dark:text-white px-1 rounded">
                            {result.lineContent.substring(result.matchStart, result.matchEnd)}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400">{result.lineContent.substring(result.matchEnd)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderFileTree = () => {
    const tree = buildFileTree(sortedFileNames);
    const filteredTree = filterTreeBySearch(tree, searchQuery);

    return (
      <div
        role="tree"
        aria-label="File explorer"
        className="focus:outline-none flex-1 overflow-y-auto"
        tabIndex={-1}
      >
        {filteredTree.map((node) => renderTreeNode(node))}
        {/* Clickable area below files to reset active folder to root */}
        <div
          className={clsx("grow min-h-[40px] cursor-pointer", dropTarget === "" && "bg-brand/20 dark:bg-brand/30")}
          style={{ height: "100%" }}
          onClick={() => setActiveFolder(null)}
          title="Click to set root directory as active folder"
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={(e) => {
            e.preventDefault();
            setDropTarget("");
          }}
          onDragLeave={() => setDropTarget(null)}
          onDrop={(e) => {
            e.preventDefault();
            const sourcePath = e.dataTransfer.getData("text/plain");
            moveNode(sourcePath, "");
            setDropTarget(null);
          }}
        />
      </div>
    );
  };

  const handleStreamingTestGeneration = async () => {
    if (readOnly) {
      const agentId = getAgentIdFromPath();
      if (!agentId) return;
      try {
        const newFunction = await dispatch(
          agentFunctionActions.create({
            agentId,
            code: agentFunctionFormData.code,
            language: agentFunctionFormData.language || language,
            name: agentFunctionFormData.name,
            isMultifile: (agentFunctionFormData as any).isMultifile,
          }) as any,
        ).unwrap();
        const previousEnvironment = (agentEnvironments as any[]).find((e) => e.functionId === functionId);
        navigate(`/agents/${agentId}/function?f=${newFunction.id}`, {
          replace: false,
          state: { autoSuggestTests: true, targetOutcome, previousEnvironmentId: previousEnvironment?.id },
        });
      } catch (e) {
        console.error("Failed to create new function version:", e);
      }
      return;
    }

    await handleAssistantStreamingRequest(
      "generate-tests",
      {
        targetOutcome: targetOutcome || undefined,
        divergedFiles: Array.from(divergedFiles),
      },
      setIsSuggestingTests,
      async (zipData, cost, duration) => {
        await updateFilesWithDiff(zipData);
        // Update the test generation result with actual cost and duration
        setAssistantGenerationResult((prev) => (prev ? { ...prev, cost, duration } : null));
      },
    );
  };

  const handleGenerateOutcomes = async () => {
    if (readOnly) {
      const agentId = getAgentIdFromPath();
      if (!agentId) return;
      try {
        const newFunction = await dispatch(
          agentFunctionActions.create({
            agentId,
            code: agentFunctionFormData.code,
            language: agentFunctionFormData.language || language,
            name: agentFunctionFormData.name,
            isMultifile: (agentFunctionFormData as any).isMultifile,
          }) as any,
        ).unwrap();
        const previousEnvironment = (agentEnvironments as any[]).find((e) => e.functionId === functionId);
        navigate(`/agents/${agentId}/function?f=${newFunction.id}`, {
          replace: false,
          state: { autoAddOutcomes: true, previousEnvironmentId: previousEnvironment?.id },
        });
      } catch (e) {
        console.error("Failed to create new function version:", e);
      }
      return;
    }

    await handleAssistantStreamingRequest(
      "generate-outcomes",
      {
        divergedFiles: Array.from(divergedFiles),
      },
      setIsAddingOutcomes,
      async (zipData, cost, duration) => {
        await updateFilesWithDiff(zipData);
        // Update the test generation result with actual cost and duration
        setAssistantGenerationResult((prev) => (prev ? { ...prev, cost, duration } : null));
      },
    );
  };

  const handleImproveFunctionFromTest = async () => {
    if (!improveTestName.trim() || isAssistantGenerating) return;

    if (readOnly) {
      const agentId = getAgentIdFromPath();
      if (!agentId) return;

      try {
        const newFunction = await dispatch(
          agentFunctionActions.create({
            agentId,
            code: agentFunctionFormData.code,
            language: agentFunctionFormData.language || language,
            name: agentFunctionFormData.name,
            isMultifile: (agentFunctionFormData as any).isMultifile,
          }) as any,
        ).unwrap();

        // Switch selection to the new function and auto-run improve on it
        const previousEnvironment = (agentEnvironments as any[]).find((e) => e.functionId === functionId);
        navigate(`/agents/${agentId}/function?f=${newFunction.id}`, {
          replace: false,
          state: { autoImproveFromTest: true, testName: improveTestName.trim(), previousEnvironmentId: previousEnvironment?.id },
        });
      } catch (e) {
        console.error("Failed to create new function version:", e);
      }
      return;
    }

    await handleAssistantStreamingRequest(
      "improve-function",
      {
        testName: improveTestName.trim(),
        divergedFiles: Array.from(divergedFiles),
      },
      setIsImprovingFunction,
      async (zipData, cost, duration) => {
        await updateFilesWithDiff(zipData);
        setAssistantGenerationResult((prev) => (prev ? { ...prev, cost, duration } : null));
      },
    );
  };

  const handleEditFunction = async () => {
    if (!assistantPrompt.trim() || isAssistantGenerating) return;

    // Store the prompt content before any state changes
    const promptContent = assistantPrompt;

    if (readOnly) {
      const agentId = getAgentIdFromPath();
      if (!agentId) return;
      try {
        const newFunction = await dispatch(
          agentFunctionActions.create({
            agentId,
            code: agentFunctionFormData.code,
            language: agentFunctionFormData.language || language,
            name: agentFunctionFormData.name,
            isMultifile: (agentFunctionFormData as any).isMultifile,
          }) as any,
        ).unwrap();
        const previousEnvironment = (agentEnvironments as any[]).find((e) => e.functionId === functionId);
        navigate(`/agents/${agentId}/function?f=${newFunction.id}`, {
          replace: false,
          state: { autoEditFunction: true, userPrompt: promptContent, previousEnvironmentId: previousEnvironment?.id },
        });
      } catch (e) {
        console.error("Failed to create new function version:", e);
      }
      return;
    }

    // Clear the input immediately only when executing locally
    setAssistantPrompt("");

    await handleAssistantStreamingRequest(
      "edit-function",
      {
        userPrompt: promptContent,
        filePath: activeFile || "handler.py",
        language: currentFileData?.language || "python",
        resetConversation: hasRejectedChanges,
        divergedFiles: Array.from(divergedFiles),
      },
      setIsEditingFunction,
      async (zipData, cost, duration) => {
        await updateFilesWithDiff(zipData);
        // Update the test generation result with actual cost and duration
        setAssistantGenerationResult((prev) => (prev ? { ...prev, cost, duration } : null));
        // Clear the rejection flag after sending it
        setHasRejectedChanges(false);
      },
    );
  };

  return (
    <View
      className="relative flex flex-col flex-grow h-full overflow-hidden"
      ref={containerRef}
    >
      {showAutoDeployDialog && (
        <Dialog
          isOpen={showAutoDeployDialog}
          onClose={handleAutoDeployCancel}
          title="Deploy New Function Version"
          actions={
            <>
              <Button
                variant="neutral"
                onClick={handleAutoDeployCancel}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAutoDeployToDebug}
                isDisabled={!debugEnv}
              >
                Debug
              </Button>
              <Button
                onClick={handleAutoDeployToPrevious}
                isDisabled={!previousEnv}
              >
                Deploy
              </Button>
            </>
          }
        >
          <View className="p-4">
            <div className="mb-2">Would you like to deploy this new function to an environment?</div>
            <ul className="list-disc ml-6 text-sm text-gray-600 dark:text-gray-300">
              <li>
                <strong>Deploy</strong>: Deploys to the same environment as the previous version
                {previousEnv ? ` (${(previousEnv as any).name})` : " (no previous environment found)"}.
              </li>
              <li>
                <strong>Debug</strong>: Deploys to the Debug environment
                {debugEnv ? ` (${(debugEnv as any).name})` : " (no debug environment)"}.
              </li>
              <li>
                <strong>Cancel</strong>: Skip deployment for now.
              </li>
            </ul>
          </View>
        </Dialog>
      )}
      <View className="flex flex-col flex-grow z-0">
        <div className="flex flex-1 overflow-hidden">
          {/* File Explorer Sidebar */}
          {showFileExplorer && (
            <div
              className="flex-shrink-0 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col relative"
              style={{ width: `${sidebarWidth}px` }}
            >
              <div className="p-2 pb-3 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Files</h3>
                  <div className="flex items-center gap-1">
                    <Button
                      onClick={() => {
                        setModalFileName("");
                        setShowAddFileModal(true);
                      }}
                      className="p-1"
                      type="button"
                      variant="ghost"
                      title="Add new file"
                    >
                      <Icon
                        name="add"
                        size={16}
                      />
                    </Button>
                    <Button
                      onClick={downloadProject}
                      className="p-1"
                      type="button"
                      variant="ghost"
                      title="Download project as ZIP"
                    >
                      <Icon
                        name="download"
                        size={16}
                      />
                    </Button>
                    <Button
                      onClick={() => setShowFileExplorer(false)}
                      className="p-1"
                      title="Hide sidebar"
                      type="button"
                      variant="ghost"
                    >
                      <Icon
                        name="chevron-left"
                        size={16}
                      />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <select
                      value={searchMode}
                      onChange={(e) => {
                        setSearchMode(e.target.value as "files" | "content");
                        setSearchQuery(""); // Clear search when switching modes
                      }}
                      className="text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-1 text-gray-900 dark:text-white"
                    >
                      <option value="content">Find in Files</option>
                      <option value="files">File Names</option>
                    </select>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={searchMode === "files" ? "Search file names..." : "Find in files..."}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-7 pr-2 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs text-gray-900 dark:text-white"
                    />
                    <Icon
                      name="search"
                      size={16}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-500 dark:text-gray-400"
                    />
                  </div>
                </div>
              </div>
              <div className="flex-1 flex flex-col min-w-0 overflow-hidden">{showSearchResults ? renderSearchResults() : renderFileTree()}</div>
              <div className="p-1.5 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <Icon
                    name="settings"
                    size={16}
                  />
                  <select
                    value={fontSize}
                    onChange={(e) => setFontSize(Number(e.target.value))}
                    className="h-5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-1 text-gray-900 dark:text-white"
                  >
                    <option value={12}>12px</option>
                    <option value={14}>14px</option>
                    <option value={16}>16px</option>
                    <option value={18}>18px</option>
                  </select>
                </div>
              </div>

              {/* Resize Handle */}
              <div
                className={`absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-brand/50 transition-colors ${
                  isResizing ? "bg-brand" : "bg-transparent"
                }`}
                onMouseDown={startResize}
                title="Drag to resize sidebar"
              />
            </div>
          )}

          {/* Main Editor Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* File Tabs */}
            <View className="bg-gray-50 dark:bg-gray-800 flex items-center overflow-x-auto no-scrollbar border-b border-gray-200 dark:border-gray-700">
              {/* Show expand button in tab bar when sidebar is hidden */}
              {!showFileExplorer && (
                <Button
                  onClick={() => setShowFileExplorer(true)}
                  className="p-1 mx-2"
                  title="Show sidebar"
                  type="button"
                  variant="ghost"
                >
                  <Icon
                    name="chevron-right"
                    size={16}
                  />
                </Button>
              )}
              {openTabs.map((filename) => (
                <div
                  key={filename}
                  draggable={true}
                  onDragStart={(e) => {
                    e.dataTransfer.setData("text/plain", filename);
                    setTabDropTarget(null);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    const rect = e.currentTarget.getBoundingClientRect();
                    const offsetX = e.clientX - rect.left;
                    const halfWidth = rect.width / 2;
                    const position = offsetX < halfWidth ? "before" : "after";
                    setTabDropTarget({ filename, position });
                  }}
                  onDragEnter={(e) => e.preventDefault()}
                  onDragLeave={() => setTabDropTarget(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (!tabDropTarget) return;
                    const draggedFilename = e.dataTransfer.getData("text/plain");
                    if (draggedFilename === tabDropTarget.filename) return;

                    setOpenTabs((prev) => {
                      const newTabs = [...prev];
                      const draggedIndex = newTabs.indexOf(draggedFilename);
                      let targetIndex = newTabs.indexOf(tabDropTarget.filename);

                      if (draggedIndex !== -1 && targetIndex !== -1) {
                        // Remove dragged tab
                        newTabs.splice(draggedIndex, 1);
                        // Adjust index for after position
                        if (tabDropTarget.position === "after") {
                          targetIndex += 1;
                        }
                        // Insert at adjusted position
                        newTabs.splice(targetIndex, 0, draggedFilename);
                      }

                      return newTabs;
                    });

                    setTabDropTarget(null);
                  }}
                  className={clsx(
                    "flex items-center px-4 py-1 border-r border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700",
                    activeFile === filename ? "bg-white dark:bg-gray-700 border-b-2 border-blue-500" : "",
                    tabDropTarget?.filename === filename && tabDropTarget.position === "before" && "!border-l-2 !border-l-brand",
                    tabDropTarget?.filename === filename && tabDropTarget.position === "after" && "!border-r-2 !border-r-blue-500",
                  )}
                  onClick={() => handleActiveFileChange(filename, false)}
                >
                  <Icon
                    name="file"
                    size={14}
                    className="mr-2 text-gray-600 dark:text-gray-400"
                  />
                  <span className="mr-1 text-sm text-gray-900 dark:text-white">{filename.split("/").pop()}</span>
                  <Button
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      closeTab(filename);
                    }}
                    size="icon"
                    className="!h-6 !w-6 !p-0 !m-0"
                    title="Close tab"
                    type="button"
                    variant="ghost"
                  >
                    <Icon
                      name="close"
                      size={16}
                      className="text-gray-600 dark:text-gray-400"
                    />
                  </Button>
                </div>
              ))}
            </View>

            {/* Editor */}
            <View className="flex-1">
              {openTabs.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full bg-gray-50 dark:bg-gray-800">
                  <div className="text-center max-w-md mx-auto p-6">
                    <div className="mb-4">
                      <Icon
                        name="folder"
                        size={64}
                        className="text-gray-400 dark:text-gray-500 mx-auto"
                      />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No files open</h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      Select a file to the left to open, create a new file using the + button in the navigation bar, or upload an entire project folder to get
                      started.
                    </p>
                    <Button
                      variant="brand"
                      onClick={() => setShowProjectUploadModal(true)}
                      className="mb-2 mx-auto"
                    >
                      Upload Project Folder
                    </Button>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mb-8">This will replace all current files with the uploaded project contents.</p>

                    {isAdmin && (
                      <>
                        <Button
                          variant="info"
                          onClick={() => setShowGenerateFunctionModal(true)}
                          className="mb-2 mx-auto"
                        >
                          Generate function with VoiceRun Assistant
                        </Button>
                        <p className="text-xs text-gray-500 dark:text-gray-500">This will replace all current files with a generated function.</p>
                      </>
                    )}
                  </div>
                </div>
              ) : showEditor ? (
                <Editor
                  key={activeFile} // Force re-render when active file changes
                  height="100%"
                  language={currentFileData?.language || language}
                  value={currentFileData?.content || ""}
                  onChange={handleFileChange}
                  onMount={handleEditorDidMount}
                  theme={theme === "dark" ? "vs-dark" : "light"}
                  options={{
                    fontSize: fontSize,
                    fontFamily: "JetBrains Mono, Consolas, Monaco, monospace",
                    minimap: { enabled: true },
                    wordWrap: "on",
                    automaticLayout: true,
                    scrollBeyondLastLine: true,
                    tabSize: 4,
                    insertSpaces: true,
                    renderLineHighlight: "all",
                    readOnly,
                    readOnlyMessage: {
                      value: `Only the latest version can be edited, and deployed versions cannot be edited.${latestNonDeployedVersionName ? ` You can edit "${latestNonDeployedVersionName}" or create a new version.` : " Create a new version to make changes."}`,
                    },
                    bracketPairColorization: { enabled: true },
                    guides: {
                      bracketPairs: true,
                      indentation: true,
                    },
                    suggest: {
                      showKeywords: true,
                      showSnippets: true,
                    },
                    quickSuggestions: {
                      other: true,
                      comments: true,
                      strings: true,
                    },
                    suggestOnTriggerCharacters: true,
                    parameterHints: { enabled: true },
                    snippetSuggestions: "top",
                    formatOnPaste: true,
                    formatOnType: true,
                    scrollbar: {
                      vertical: "visible",
                      horizontal: "visible",
                      useShadows: false,
                      verticalScrollbarSize: 10,
                      horizontalScrollbarSize: 10,
                      verticalSliderSize: 10,
                      horizontalSliderSize: 10,
                      arrowSize: 30,
                    },
                  }}
                />
              ) : (
                <View className="w-full h-full flex-1 bg-black dark:bg-[#1e1e1e] transition-all duration-100 dark:duration-200 opacity-25 dark:opacity-100" />
              )}
            </View>

            {/* Test Generation Notification Bar */}
            {assistantGenerationResult?.visible && (
              <div className="absolute bottom-12 left-48 right-0 bg-blue-50 dark:bg-blue-900/20 border-t border-blue-200 dark:border-blue-800 z-10">
                <div className="px-4 py-3 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <Icon
                      name="check-circle"
                      size={20}
                      className="text-blue-600 dark:text-blue-400"
                    />
                    <div>
                      <div className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Tests generated successfully! {assistantGenerationResult.changedFiles.length} file(s) modified
                      </div>
                      {assistantGenerationResult.changedFiles.length > 1 && (
                        <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                          Changed files: {assistantGenerationResult.labeledChangedFiles?.join(", ") || assistantGenerationResult.changedFiles.join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handleRejectGeneration}
                      variant="danger"
                      className="text-xs px-3 py-1 bg-red-600 hover:bg-red-700 text-white border-red-600 hover:border-red-700"
                    >
                      Reject
                    </Button>
                    <Button
                      onClick={handleAcceptGeneration}
                      variant="success"
                      className="text-xs px-3 py-1 bg-green-600 hover:bg-green-700 text-white border-green-600 hover:border-green-700"
                    >
                      Accept
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Status Bar */}
            <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2 text-xs border-t border-gray-200 dark:border-gray-700 flex justify-between items-center h-12">
              <div className="flex items-center gap-4">
                <span className="text-gray-600 dark:text-gray-400">File: {activeFile}</span>
                <span className="text-gray-600 dark:text-gray-400">Language: {currentFileData?.language || "none"}</span>
                <span className="text-gray-600 dark:text-gray-400">Open Tabs: {openTabs.length}</span>
                <span className="text-gray-600 dark:text-gray-400">Total Files: {Object.keys(files).length}</span>
              </div>
              <div className="flex items-center gap-4">
                {isAdmin && (
                  <Button
                    onClick={() => setShowAssistantSidebar(true)}
                    variant="info"
                    className="text-xs"
                  >
                    Open VoiceRun Assistant
                  </Button>
                )}
                <span className="text-gray-600 dark:text-gray-400">Theme: {theme}</span>
                <span className="text-gray-600 dark:text-gray-400">Font: {fontSize}px</span>
              </div>
            </div>
          </div>
        </div>
      </View>

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg py-1 min-w-[120px]"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
          }}
        >
          {!isFolderPath(contextMenu.targetFile) && (
            <button
              onClick={handleRenameFile}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            >
              <span>Rename</span>
            </button>
          )}
          <button
            onClick={handleDeleteFile}
            className={clsx(
              "w-full px-4 py-2 text-left text-sm flex items-center gap-2",
              contextMenu.targetFile === `handler.${language === "python" ? "py" : "js"}`
                ? "text-gray-400 dark:text-gray-600 cursor-not-allowed"
                : "text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700",
            )}
            disabled={contextMenu.targetFile === `handler.${language === "python" ? "py" : "js"}`}
          >
            <span>Delete</span>
          </button>
        </div>
      )}

      {/* Add File Modal */}
      <Dialog
        isOpen={showAddFileModal}
        onClose={() => {
          setShowAddFileModal(false);
          setModalFileName("");
          setUploadedFile(null);
        }}
        title="Add New File"
        size="md"
        actions={
          <div className="flex gap-2">
            <Button
              variant="neutral"
              onClick={() => {
                setShowAddFileModal(false);
                setModalFileName("");
                setUploadedFile(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="brand"
              onClick={async () => {
                if (modalFileName.trim()) {
                  if (modalTab === "upload" && uploadedFile) {
                    // Read file content and add it
                    const reader = new FileReader();
                    reader.onload = async (e) => {
                      const content = e.target?.result as string;
                      await addNewFile(modalFileName.trim(), content);
                    };
                    reader.readAsText(uploadedFile);
                  } else {
                    // Create empty file
                    await addNewFile(modalFileName.trim());
                  }
                }
              }}
              isDisabled={!modalFileName.trim() || (modalTab === "upload" && !uploadedFile)}
            >
              Add File
            </Button>
          </div>
        }
      >
        <div className="p-4">
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
            <button
              onClick={() => setModalTab("create")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                modalTab === "create"
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              Create New
            </button>
            <button
              onClick={() => setModalTab("upload")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                modalTab === "upload"
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              Upload File
            </button>
          </div>

          {/* Tab Content */}
          {modalTab === "create" ? (
            <div>
              <InputField
                label="File Name"
                placeholder={`filename.${language === "python" ? "py" : "js"}`}
                value={modalFileName}
                onChange={(val) => {
                  setModalFileName(val);
                  setAddFileError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && modalFileName.trim()) {
                    addNewFile(modalFileName.trim());
                  } else if (e.key === "Escape") {
                    setShowAddFileModal(false);
                    setModalFileName("");
                    setAddFileError("");
                  }
                }}
              />
              {addFileError && <div className="text-xs text-red-600 mt-1">{addFileError}</div>}
              {activeFolder && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  File will be created in: <span className="font-medium">{activeFolder}/</span>
                </div>
              )}
            </div>
          ) : (
            <div>
              <InputField
                label="File Name"
                placeholder="Enter filename (optional - will use uploaded file name)"
                value={modalFileName}
                onChange={(val) => {
                  setModalFileName(val);
                  setAddFileError("");
                }}
              />
              {addFileError && <div className="text-xs text-red-600 mt-1">{addFileError}</div>}
              <div className="mt-4">
                <FileField
                  onChange={handleFileUpload}
                  fileTypes={["py", "js", "ts", "jsx", "tsx", "json", "yml", "yaml", "html", "css", "md", "sql", "xml", "txt"]}
                  size="md"
                />
              </div>
              {uploadedFile && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Selected: <span className="font-medium">{uploadedFile.name}</span>
                  {!modalFileName && (
                    <div className="mt-1">
                      Will use: <span className="font-medium">{uploadedFile.name}</span>
                    </div>
                  )}
                </div>
              )}
              {activeFolder && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  File will be created in: <span className="font-medium">{activeFolder}/</span>
                </div>
              )}
            </div>
          )}
        </div>
      </Dialog>

      {/* Project Upload Modal */}
      <Dialog
        isOpen={showProjectUploadModal}
        onClose={() => {
          setShowProjectUploadModal(false);
          setUploadedProject(null);
        }}
        title="Upload Project Folder"
        size="lg"
        actions={
          <div className="flex gap-2">
            <Button
              variant="neutral"
              onClick={() => {
                setShowProjectUploadModal(false);
                setUploadedProject(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="brand"
              onClick={uploadProject}
              isDisabled={!uploadedProject || uploadedProject.length === 0}
            >
              Upload Project
            </Button>
          </div>
        }
      >
        <div className="p-4">
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Select a folder containing your project files. This will replace all current files with the uploaded project contents.
            </p>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Warning:</strong> This action will delete all current files and replace them with the uploaded project.
              </p>
            </div>
          </div>

          <div className="mt-4">
            <FolderUploadField
              onChange={handleProjectUpload}
              size="lg"
            />
          </div>

          {uploadedProject && uploadedProject.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Selected Files ({uploadedProject.length}):</h4>
              <div className="max-h-32 overflow-y-auto bg-gray-50 dark:bg-gray-800 rounded border p-2">
                {Array.from(uploadedProject).map((file, index) => (
                  <div
                    key={index}
                    className="text-xs text-gray-600 dark:text-gray-400 py-1"
                  >
                    {file.webkitRelativePath || file.name}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Dialog>

      <ConfirmDialog
        isOpen={showDeleteFolderDialog}
        onClose={() => {
          if (isDeletingFolder) return;
          setShowDeleteFolderDialog(false);
          setFolderToDelete(null);
        }}
        onConfirm={async () => {
          if (!folderToDelete) return;
          setIsDeletingFolder(true);
          try {
            await removeFolder(folderToDelete);
            setShowDeleteFolderDialog(false);
            setFolderToDelete(null);
          } finally {
            setIsDeletingFolder(false);
          }
        }}
        title="Delete Folder"
        message={folderToDelete ? `Are you sure you want to delete the folder "${folderToDelete}"?` : "Are you sure you want to delete this folder?"}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={isDeletingFolder}
        variant="danger"
      >
        <Text className="text-sm text-red-600 dark:text-red-400 font-semibold">All files and subfolders within this folder will also be deleted.</Text>
      </ConfirmDialog>

      {/* Generate Function Modal */}
      <Dialog
        isOpen={showGenerateFunctionModal}
        onClose={() => {
          setShowGenerateFunctionModal(false);
          setGenerateFunctionPrompt("");
        }}
        title="Generate Function with VoiceRun Assistant"
        size="lg"
        actions={
          <div className="flex gap-2">
            <Button
              variant="neutral"
              onClick={() => {
                setShowGenerateFunctionModal(false);
                setGenerateFunctionPrompt("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={handleGenerateFunction}
              isDisabled={!generateFunctionPrompt.trim() || isGeneratingFunction || isAssistantGenerating}
            >
              {isGeneratingFunction ? "Generating..." : "Generate Function"}
            </Button>
          </div>
        }
      >
        <div className="p-4">
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Describe what you want your agent function to do. VoiceRun Assistant will create a complete function based on your prompt and the agent's name and
              description.
            </p>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Warning:</strong> This action will delete all current files and replace them with a generated function.
              </p>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Describe your agent function</label>
            <textarea
              value={generateFunctionPrompt}
              onChange={(e) => setGenerateFunctionPrompt(e.target.value)}
              placeholder="Describe what your agent should do. For example: 'Create a customer service agent that can handle product inquiries, process returns, and escalate complex issues to a supervisor.'"
              className="w-full h-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              disabled={isGeneratingFunction}
            />
          </div>

          {isGeneratingFunction && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <p className="text-sm text-blue-800 dark:text-blue-200">Generating your function with VoiceRun Assistant...</p>
              </div>
            </div>
          )}
        </div>
      </Dialog>

      {/* Assistant Sidebar */}
      {showAssistantSidebar && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowAssistantSidebar(false)}
          />

          {/* Sidebar */}
          <div className="relative ml-auto w-96 h-full bg-white dark:bg-gray-800 shadow-xl flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">VoiceRun Assistant</h3>
              <button
                onClick={() => setShowAssistantSidebar(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <Icon
                  name="x"
                  size={20}
                />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={assistantMessagesRef}
              className="flex-1 overflow-y-auto p-4 space-y-4"
            >
              {assistantMessages.length === 0 ? (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  <p>Start a conversation with VoiceRun Assistant</p>
                </div>
              ) : (
                assistantMessages.map((message, index) => {
                  if (message.type === "system") {
                    return (
                      <div
                        key={index}
                        className="flex justify-center"
                      >
                        <div className="text-xs text-gray-500 dark:text-gray-400 text-center max-w-xs">{message.content}</div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={index}
                      className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-md px-3 py-2 rounded-lg break-words overflow-hidden ${
                          message.type === "user" ? "bg-blue-500 text-white" : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                        }`}
                      >
                        {message.type === "assistant" ? (
                          <div className="text-sm prose prose-sm dark:prose-invert max-w-none overflow-hidden">
                            <ReactMarkdown
                              components={{
                                code: ({ node, className, children, ...props }: any) => {
                                  const match = /language-(\w+)/.exec(className || "");
                                  const isInline = !className || !className.includes("language-");
                                  return !isInline ? (
                                    <pre className="bg-gray-800 text-gray-100 p-2 rounded text-xs overflow-x-auto max-w-full">
                                      <code
                                        className={className}
                                        {...props}
                                      >
                                        {children}
                                      </code>
                                    </pre>
                                  ) : (
                                    <code
                                      className="bg-gray-200 dark:bg-gray-600 px-1 py-0.5 rounded text-xs"
                                      {...props}
                                    >
                                      {children}
                                    </code>
                                  );
                                },
                                pre: ({ children }: any) => <div className="overflow-x-auto max-w-full">{children}</div>,
                              }}
                            >
                              {message.content}
                            </ReactMarkdown>
                          </div>
                        ) : (
                          <p className="text-sm break-words">{message.content}</p>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Input Area */}
            <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4">
              {/* Prompt Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Edit with VoiceRun Assistant</label>
                <div className="relative">
                  <textarea
                    value={assistantPrompt}
                    onChange={(e) => setAssistantPrompt(e.target.value)}
                    placeholder="Edit your function with VoiceRun Assistant..."
                    className="w-full px-3 py-2 pr-12 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white resize-none"
                    rows={3}
                    disabled={isEditingFunction || isAssistantGenerating}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleAssistantPrompt();
                      }
                    }}
                  />
                  <button
                    onClick={handleAssistantPrompt}
                    disabled={!assistantPrompt.trim() || isEditingFunction || isAssistantGenerating}
                    className="absolute right-2 bottom-2 p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Icon
                      name="arrow-up"
                      size={16}
                    />
                  </button>
                </div>
              </div>

              {/* Suggest Tests */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Suggest Tests</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={targetOutcome}
                    onChange={(e) => setTargetOutcome(e.target.value)}
                    placeholder="Target outcome (optional)"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                  <Button
                    onClick={handleStreamingTestGeneration}
                    variant="info"
                    className="self-end text-xs"
                    isDisabled={!activeFile || isSuggestingTests || isAssistantGenerating}
                    isLoading={isSuggestingTests}
                    loadingText=""
                  >
                    Suggest
                  </Button>
                </div>
              </div>

              {/* Add Outcomes */}
              <div>
                <Button
                  onClick={handleAddOutcomes}
                  variant="info"
                  className="w-full text-sm"
                  isDisabled={isAddingOutcomes || isAssistantGenerating}
                  isLoading={isAddingOutcomes}
                  loadingText=""
                >
                  Add Outcomes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </View>
  );
};
